import type { Components, Theme } from '@mui/material/styles';
import { ArrowDown, CaretDown, X, XCircle } from '@phosphor-icons/react';
import { createElement } from 'react';
// Type-only side-effect import: augments the MUI theme `Components` map with the MUI X
// date/time picker keys (MuiPickerDay, MuiDateCalendar, MuiPickersCalendarHeader, …) so the
// picker overrides below are type-checked.
import '@mui/x-date-pickers/themeAugmentation';
import { AvatarFallback } from '../components/Avatar/AvatarFallback.tsx';
import { PickerField } from '../components/DatePicker/PickerField.tsx';
import {
  PickerCalendarIcon,
  PickerClockIcon,
  PickerLeftArrowIcon,
  PickerRightArrowIcon,
  PickerSwitchViewIcon,
} from '../components/DatePicker/PickerSlotIcon.tsx';
import { RadioCheckedIcon, RadioUncheckedIcon } from '../components/RadioGroup/RadioIcons.tsx';
import {
  CHIP_LABEL_INSET,
  chipCircle,
  chipCircleDisabled,
  chipCircleDisabledSelected,
  chipCircleSelected,
  chipDisabled,
  chipDisabledSelected,
  chipGlyph,
  chipIconSlot,
  chipIconSvgSize,
  chipIndicatorBox,
  chipLoaderSlot,
  chipSelected,
  chipShell,
  chipSize,
} from '../components/SelectionChip/selectionChipStyles.ts';
import { StepperIcon } from '../components/Stepper/StepperIcon.tsx';
import {
  intent,
  radius,
  shadowTokens,
  sizes,
  spacingTokens,
  state,
  stateAlpha,
  surface,
  text,
  typography,
} from './tokens/index.ts';

/**
 * Per-component theme customizations: `defaultProps`, `styleOverrides`, `variants`.
 *
 * Overrides are added ONE component at a time, only after that component's tokens exist,
 * following the theme-first / token-first workflow in CLAUDE.md.
 *
 * Rules when populating this file:
 * - Every value MUST reference a token from `./tokens` (HARD RULE 1) — no raw hex/px.
 *   The `lint:tokens` guardrail scans this file and will fail on raw values.
 * - Prefer overriding the specific component (e.g. `MuiButton`). Changing a SHARED primitive
 *   (e.g. `MuiPaper`, `MuiButtonBase`) affects every component that uses it — get approval
 *   first and record the blast radius in docs/DECISIONS.md (see "Sub-component awareness").
 */
/**
 * Date navigation goes **year → month → day**, on every component that picks a date.
 *
 * MUI X defaults to `['year', 'day']` — picking a year drops the user straight back into a day
 * grid, so there is no way to reach a distant month except by paging the header one month at a
 * time. Inserting the month view makes the drill-down complete and makes each step narrow the
 * one below it. Set here rather than per call site so the standalone `DateCalendar`, the popover
 * pickers and the date filter can't drift apart (designer, 2026-09-16 — see docs/DECISIONS.md).
 *
 * `openTo` is deliberately NOT set: it stays MUI's `'day'`, so opening a picker still lands on
 * the day grid and the extra view costs the common case nothing — it exists on the way back up.
 */
const DATE_VIEWS = ['year', 'month', 'day'] as const;

/** The same scale for the date half of DateTimePicker; the time views follow it unchanged. */
const DATE_TIME_VIEWS = ['year', 'month', 'day', 'hours', 'minutes'] as const;

export const components: Components<Theme> = {
  MuiCssBaseline: {
    // SHARED — affects the entire document. Ensures Figtree propagates to form elements
    // (input, button, textarea, select) which browsers don't inherit font-family from body.
    // MUI's CssBaseline already sets body { font-family } from theme.typography.fontFamily;
    // this covers the remaining elements that opt out of inheritance by default.
    styleOverrides: `
      input, button, textarea, select {
        font-family: inherit;
      }
    `,
  },
  MuiCheckbox: {
    defaultProps: {
      // Explicit default — aligns with palette.primary.main = intent.primary.solid (purple).
      color: 'primary',
    },
    styleOverrides: {
      root: {
        // Touch target: h28 × h28. Explicit width/height so the hover oval is always exactly
        // h28 (the MUI default border-radius:50% on the root creates the circle).
        // box-sizing: border-box (MUI default) — padding is included inside h28.
        // Padding = (h28 − h24) / 2 = 2 centres the h24 SVG within the h28 container.
        // CheckBoxOutlineBlankIcon path: coords 3–21 in a 24-unit viewBox = 18 units → 18px at 1:1 scale.
        width: sizes.height.h28,
        height: sizes.height.h28,
        padding: (sizes.height.h28 - sizes.height.h24) / 2,
        '& .MuiSvgIcon-root': {
          fontSize: sizes.height.h24,
        },

        // Only primary is supported — all overrides are scoped to .MuiCheckbox-colorPrimary.
        '&.MuiCheckbox-colorPrimary': {
          // Hover: primary subtle (Figma: intent/primary/subtle = purple-008).
          // The h28 root + border-radius:50% (MUI default) gives exactly the h28 hover oval.
          '&:not(.Mui-disabled):hover': {
            backgroundColor: intent.primary.subtle,
          },

          // Unchecked enabled: white fill + 1px neutral border (Figma: base/white + intent/neutral/border).
          // MUI's CheckBoxOutlineBlankIcon is a single-colour SVG path — cannot separately fill
          // white and draw a 1px inside border. Hide the SVG; render ::before at explicit h18 × h18.
          // top/left = (h28 − h18) / 2 = 5. Explicit width/height avoids right/bottom depending
          // on containing-block size resolution.
          '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate):not(.Mui-disabled)': {
            '& .MuiSvgIcon-root': { opacity: 0 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: (sizes.height.h28 - sizes.height.h18) / 2,
              left: (sizes.height.h28 - sizes.height.h18) / 2,
              width: sizes.height.h18,
              height: sizes.height.h18,
              backgroundColor: surface.white,
              boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.neutral.border}`,
              borderRadius: radius.sm,
              pointerEvents: 'none',
            },
          },

          // Disabled unchecked: same ::before approach — neutral disabled fill + disabled border.
          '&.Mui-disabled:not(.Mui-checked):not(.MuiCheckbox-indeterminate)': {
            '& .MuiSvgIcon-root': { opacity: 0 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: (sizes.height.h28 - sizes.height.h18) / 2,
              left: (sizes.height.h28 - sizes.height.h18) / 2,
              width: sizes.height.h18,
              height: sizes.height.h18,
              backgroundColor: intent.neutral.disabled,
              boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${state.disabled.border}`,
              borderRadius: radius.sm,
              pointerEvents: 'none',
            },
          },

          // Disabled checked / indeterminate: muted primary fill.
          '&.Mui-disabled.Mui-checked, &.Mui-disabled.MuiCheckbox-indeterminate': {
            color: intent.primary.disabled,
          },
        },
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      // Explicit default — aligns with palette.primary.main = intent.primary.solid (purple).
      color: 'primary',
      // Custom icons: outer ring h18 (18px), inner dot h12 (12px).
      // MUI's built-in RadioButtonUncheckedIcon/RadioButtonCheckedIcon scale both icons with
      // the same fontSize, making independent sizing impossible. Custom SVGs solve this.
      icon: createElement(RadioUncheckedIcon),
      checkedIcon: createElement(RadioCheckedIcon),
    },
    styleOverrides: {
      root: {
        // Touch target h28 (28px); SVG bounding box h18 (18px); padding = (h28−h18)/2 = 5px.
        // Figma: "stroke" element is 18×18 at offset left/top 5px inside the 28×28 container.
        // Radio differs from Checkbox (which uses h24) — RadioButtonUncheckedIcon is a single
        // solid-coloured ring that fills the SVG box cleanly; no path-vs-viewBox scaling needed.
        padding: (sizes.height.h28 - sizes.height.h18) / 2,
        '& .MuiSvgIcon-root': {
          fontSize: sizes.height.h18,
        },

        // Only primary is supported — overrides scoped to .MuiRadio-colorPrimary.
        '&.MuiRadio-colorPrimary': {
          // Hover: primary subtle bg, pill radius (Figma: intent/primary/subtle, rounded h-40=40px).
          '&:not(.Mui-disabled):hover': {
            backgroundColor: intent.primary.subtle,
          },

          // Unchecked + enabled: neutral ring (Figma: intent/neutral/border).
          '&:not(.Mui-checked):not(.Mui-disabled)': {
            color: intent.neutral.border,
          },

          // Disabled unchecked: neutral fill (grey006) + ring border (state.disabled.border).
          // ::before (position:absolute) paints AFTER static SVG descendants in CSS paint order,
          // so it would cover the ring. Fix: hide the SVG and render both fill and ring via
          // boxShadow inset — same pattern as Checkbox disabled unchecked.
          '&.Mui-disabled:not(.Mui-checked)': {
            '& .MuiSvgIcon-root': { opacity: 0 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: (sizes.height.h28 - sizes.height.h18) / 2,
              left: (sizes.height.h28 - sizes.height.h18) / 2,
              width: sizes.height.h18,
              height: sizes.height.h18,
              backgroundColor: intent.neutral.disabled,
              boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${state.disabled.border}`,
              borderRadius: '50%',
              pointerEvents: 'none',
            },
          },

          // Disabled checked: muted primary ring + dot (intent.primary.disabled via currentColor).
          '&.Mui-disabled.Mui-checked': {
            color: intent.primary.disabled,
          },
        },
      },
    },
  },
  MuiFormGroup: {
    // RadioGroup is NOT a styled component — it renders FormGroup internally.
    // FormGroup IS styled with name:'MuiFormGroup', so only MuiFormGroup overrides inject CSS.
    // Blast radius: Checkbox FormGroup uses the same gap (sp4 vertical); row stays sp16.
    styleOverrides: {
      root: {
        gap: spacingTokens.sp4,
        '&.MuiFormGroup-row': {
          gap: spacingTokens.sp16,
        },
      },
    },
  },
  MuiSwitch: {
    defaultProps: {
      // Explicit default — aligns with palette.primary.main = intent.primary.solid (purple).
      color: 'primary',
    },
    styleOverrides: {
      root: {
        // Figma: overall footprint 36×28px, track 28×18px.
        // Horizontal padding sp4 (4px): track width = 36 − 8 = 28px.
        // Vertical padding 5px (derived token expression): track height = 28 − 10 = 18px.
        width: sizes.height.h36,
        height: sizes.height.h28,
        paddingLeft: spacingTokens.sp4,
        paddingRight: spacingTokens.sp4,
        paddingTop: (sizes.height.h28 - sizes.height.h18) / 2,
        paddingBottom: (sizes.height.h28 - sizes.height.h18) / 2,
        // Pill border-radius clips the hover background to the full 36×28 footprint shape.
        // Hover targets the whole root (not just the thumb), matching the Figma full-width pill hover.
        // :has() prevents hover on disabled switches (switchBase carries Mui-disabled when disabled).
        borderRadius: radius.pill,
        '&:has(.MuiSwitch-switchBase:not(.Mui-disabled)):hover': {
          backgroundColor: intent.primary.subtle,
        },
      },
      // Only primary is styled — scoped to .MuiSwitch-colorPrimary on the switchBase.
      switchBase: {
        // Horizontal padding = (h28−h18)/2 = 5px: positions thumb 1px inside track left/right edge.
        // Vertical padding = (h28−h16)/2 = 6px: centers h16 thumb with 1px gap in h18 track (thumb y=1).
        // Checked translateX = h28−h18 = 10px (confirmed by Figma: thumb travels from x=1 to x=11 in track).
        // Suppress MUI ButtonBase default hover (palette.action.hover = rgba(0,0,0,0.04)) — the full-width
        // hover is handled on root instead (intent.primary.subtle over the entire 36×28 pill footprint).
        '&:hover': {
          backgroundColor: 'transparent',
        },
        paddingLeft: (sizes.height.h28 - sizes.height.h18) / 2,
        paddingRight: (sizes.height.h28 - sizes.height.h18) / 2,
        paddingTop: (sizes.height.h28 - sizes.height.h16) / 2,
        paddingBottom: (sizes.height.h28 - sizes.height.h16) / 2,
        '&.Mui-checked': {
          transform: `translateX(${sizes.height.h28 - sizes.height.h18}px)`, // tokens-ok
        },
        '&.MuiSwitch-colorPrimary': {
          // Hover is handled on root (full pill width) — no thumb-only hover here.
          // Checked: primary solid track.
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: intent.primary.solid,
            opacity: 1,
          },
          // Disabled (any state): neutral disabled track.
          '&.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: intent.neutral.disabled,
            opacity: 1,
          },
          // Disabled + checked: primary disabled track (more specific — comes after disabled rule).
          '&.Mui-disabled.Mui-checked + .MuiSwitch-track': {
            backgroundColor: intent.primary.disabled,
          },
          // Disabled unchecked: thumb = state.disabled.surface (rgba(0,0,0,0.10)).
          // Figma: disabled-off thumb is a translucent black overlay — NOT white.
          // Disabled checked thumb stays white (no override needed).
          '&.Mui-disabled:not(.Mui-checked) .MuiSwitch-thumb': {
            backgroundColor: state.disabled.surface,
          },
        },
      },
      thumb: {
        // Size h16×h16 confirmed by Figma (16×16 ellipse, 1px gap from track edge in all states).
        // Colour: white by default; disabled-unchecked overridden above via switchBase selector.
        backgroundColor: surface.white,
        width: sizes.height.h16,
        height: sizes.height.h16,
        boxShadow: 'none',
      },
      track: {
        // Unchecked enabled track: neutral border colour at full opacity.
        // Checked/disabled colours are driven via switchBase sibling CSS above.
        backgroundColor: intent.neutral.border,
        opacity: 1,
        borderRadius: radius.pill,
      },
    },
  },
  // Slider — the only interactive control whose value is a POSITION rather than a text entry.
  // Mirrored from Figma (page "Slider", set `2864:21486`): 10 variants, Type (Range | Single) ×
  // State (Default | Hover | Focus | Dragging | Disabled). Zero blast radius — every Slider slot is
  // a bare `span`/`input`, so nothing outside this component reads these rules.
  //
  // The rules below cover ONE size, ONE colour and ONE orientation on purpose: `size`, `color`
  // (beyond primary), `orientation`, `track`, `marks` and `scale` were removed from `SliderProps`
  // (designer, 2026-08-31), so styling them would be dead CSS for props that no longer compile.
  MuiSlider: {
    defaultProps: {
      // Explicit default — aligns with palette.primary.main = intent.primary.solid (purple).
      color: 'primary',
      // Figma shows the value bubble in State=Dragging. `auto` is the MUI prop that covers it
      // (drag + hover + keyboard focus, hidden at rest) — MUI's own default is `off`, which would
      // leave the value readable only as a thumb position.
      valueLabelDisplay: 'auto',
    },
    styleOverrides: {
      root: {
        // rd-40 on a 4px bar resolves to fully round ends and is inherited by both the rail and
        // the track. Replaces MUI's raw 12px.
        borderRadius: radius.pill,
        // The bar thickness IS the root's height: the rail and the track both inherit it. Figma
        // binds the rail to h-4.
        height: sizes.height.h4,
        // Figma's slider frame is h-36 (320×36) so a slider row lines up with a field row — which
        // is what keeps RangeSliderField's vertical rhythm true. MUI's own 13px padding would make
        // the box 30 and pull everything below it up by 6.
        paddingTop: (sizes.height.h36 - sizes.height.h4) / 2,
        paddingBottom: (sizes.height.h36 - sizes.height.h4) / 2,
        '@media (pointer: coarse)': {
          // MUI's coarse-pointer rule, re-declared: this override lands after it, so without
          // repeating it the touch target would shrink from 44 back to 36 on touch devices.
          paddingTop: sizes.height.h20,
          paddingBottom: sizes.height.h20,
        },
        // Disabled (Figma State=Disabled): the rail does NOT change — only the filled track and the
        // thumb's ring fade to the primary `disabled` facet, and the thumb keeps its white centre.
        // Driven through `color` because MUI paints the track background and the thumb border from
        // `currentColor`; one declaration moves both. MUI's own default here is a raw `grey[400]`,
        // which is why this cannot simply be left alone.
        '&.Mui-disabled': {
          color: intent.primary.disabled,
        },
      },
      // The UNFILLED remainder. Note MUI's naming: `rail` is the full-length background and
      // `track` is the filled part — the opposite way round from LinearProgress.
      // Figma binds it to `Intent/Neutral/Solid` (grey016) and keeps that grey even when the
      // slider is disabled, so the length of the scale stays readable. Replaces MUI's
      // `currentColor` at 38%, an untokenised opacity.
      rail: {
        backgroundColor: intent.neutral.solid,
        opacity: 1,
      },
      // The FILLED portion. Colour comes from `currentColor` = palette.primary.main =
      // intent.primary.solid, so no override is needed for the fill itself.
      track: {
        // MUI draws a 1px `currentColor` border here, which makes the fill 2px thicker than the
        // rail it sits on. Dropped so the two line up — but only where colours actually paint:
        // in forced-colors mode the border is the ONLY thing that draws the fill.
        '@media (forced-colors: none)': {
          border: 'none',
        },
      },
      // Figma's thumb is HOLLOW: a white disc with a 2px ring in the intent colour, not MUI's
      // solid puck. The ring is `currentColor`, so it fades with the disabled rule above without
      // repeating the colour here.
      thumb: {
        width: sizes.height.h20,
        height: sizes.height.h20,
        backgroundColor: surface.white,
        border: `${sizes.border.bd2}px solid currentColor`,
        // Controls are flat in this design system (elevation 0) — same call as the Switch thumb.
        // MUI's default here is `theme.shadows[2]`, one of the untokenised ladder entries.
        '&::before': { boxShadow: shadowTokens.none },
        // Hover / drag halo — `intent.primary.subtle`, the same purple tint every other selection
        // control uses for hover (Checkbox, Radio, Switch), replacing MUI's computed
        // `alpha(main, 0.16)`. Figma draws it as a concentric disc, so the spread is derived from
        // the DIAMETER Figma binds (h-32 hover, h-40 dragging) minus the thumb, halved — which is
        // also why these read as 6 and 10 rather than as spacing steps.
        // The `hover: none` guard is MUI's and is kept: on touch there is no hover to show.
        '&:hover': {
          boxShadow: `0 0 0 ${(sizes.height.h32 - sizes.height.h20) / 2}px ${intent.primary.subtle}`,
          '@media (hover: none)': { boxShadow: 'none' },
        },
        '&.Mui-active': {
          boxShadow: `0 0 0 ${(sizes.height.h40 - sizes.height.h20) / 2}px ${intent.primary.subtle}`,
        },
        // Keyboard focus (Figma State=Focus): a bd-2 `intent.primary.solid` ring sitting bd-2 clear
        // of the thumb, with the gap filled by `intent.primary.subtle` — total Ø28 (= h-28), which
        // is where Figma's "focus ring" ellipse sits. The ring is the SOLID intent, never the soft
        // `border` tint, which fails the 3:1 non-text contrast bar. Listed last so it wins over the
        // hover halo at equal specificity when a thumb is both hovered and focused.
        '&.Mui-focusVisible': {
          boxShadow: `0 0 0 ${sizes.border.bd2}px ${intent.primary.subtle}, 0 0 0 ${sizes.border.bd4}px ${intent.primary.solid}`,
        },
      },
      // The value bubble IS the DS Tooltip in Figma (an instance of it), so it takes the same
      // surface, ink, radius, padding, max-width and 10px `captionSm` type. MUI's own arrow is an
      // 8×8 square rotated 45°, which renders ~11.3×5.7 — near enough to Figma's 12×6 triangle
      // that it needs no override.
      valueLabel: {
        backgroundColor: surface.inverse,
        color: text.white,
        borderRadius: radius.sm,
        padding: `${spacingTokens.sp4}px ${spacingTokens.sp8}px`,
        maxWidth: sizes.maxWidth.xs,
        ...typography.captionSm,
      },
      // No `mark` / `markActive` / `markLabel` rules: `marks` is not part of this component's API
      // ("no tick marks by design"), so there is nothing to style.
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      variant: 'contained',
      size: 'large',
    },
    styleOverrides: {
      root: {
        // A button is always at least as wide as its label: the text never wraps and the button
        // never shrinks below its content in a flex row. When several buttons don't fit, they
        // should wrap onto the next line at the CONTAINER (give it `flex-wrap: wrap`, as
        // AccordionActions does) — never squash a button or wrap its text.
        whiteSpace: 'nowrap',
        flexShrink: 0,

        // ── Colour model (MUI v9) ────────────────────────────────────────────────────────────
        // The Button root carries variant and colour as SEPARATE classes (e.g. `MuiButton-text` +
        // `MuiButton-colorSecondary`), NOT a combined `MuiButton-textSecondary`. Any variant×colour
        // rule MUST be a COMPOUND selector — combined-class selectors match nothing in v9.
        //
        // Base colours are supplied by the MUI palette mapped in theme.ts (primary=purple,
        // secondary=navy, error=red) — contained fill, contained text (contrastText), and text-
        // variant label colour all come from there, so only the exceptions below are overridden.
        //
        // Contained hover/pressed intentionally use MUI's built-in behaviour (palette.*.dark shade
        // + ripple). The designer prefers it over a Figma-style translucent overlay (2026-07-14),
        // so there is deliberately no override here.

        // Text variant hover/pressed — neutral translucent overlay, identical for every colour
        // (Figma: text primary/secondary/error all use state/hover + state/pressed). One rule keeps
        // the three text colours consistent; the label colour never changes on hover.
        '&.MuiButton-text:not(.Mui-disabled):hover': { backgroundColor: state.hover },
        '&.MuiButton-text:not(.Mui-disabled):active': { backgroundColor: state.pressed },

        // Text secondary — neutral/grey label + icon (Figma nodes 1250:6683+). Unlike contained
        // secondary (navy), the text variant's "secondary" is the neutral `text.secondary` token;
        // MUI's default here would be navy (palette.secondary.main), so it is overridden. Icons
        // follow via currentColor, so this one declaration covers both label and glyph.
        '&.MuiButton-text.MuiButton-colorSecondary': { color: text.secondary },

        // Disabled (Figma): contained = neutral grey fill (`intent.neutral.disabled`) + disabled
        // text, for every colour; text variant = disabled text only, no fill. Declared last so the
        // disabled text colour wins the equal-specificity tie against the colour rules above.
        '&.MuiButton-contained.Mui-disabled': {
          backgroundColor: intent.neutral.disabled,
          color: text.disabled,
        },
        '&.MuiButton-text.Mui-disabled': { color: text.disabled },

        // No per-button casing exceptions. `typography.button` decides for every Button,
        // FilterBar's "Clear Filters" included (designer, 2026-10-05, withdrawing a 2026-09-22
        // sentence-case exception whose theme rule had never matched the element). The
        // `WithClearFilters` story asserts the computed `text-transform` so a casing rule that
        // silently misses, or silently lands, is caught either way.
      },

      // Figma "medium" = 28px. Override MUI's default 36px medium height and 16px font.
      sizeMedium: {
        height: sizes.height.h28,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: spacingTokens.sp8,
        paddingRight: spacingTokens.sp8,
        fontSize: typography.buttonSm.fontSize,
        lineHeight: typography.buttonSm.lineHeight,
      },

      // Figma "large" = 36px. MUI's sizeLarge overrides font to 15px — reset to token.
      sizeLarge: {
        height: sizes.height.h36,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: spacingTokens.sp8,
        paddingRight: spacingTokens.sp8,
        fontSize: typography.button.fontSize,
        lineHeight: typography.button.lineHeight,
      },

      // Icon gap = sp4 (4px) for both sizes. MuiButton-iconSize{Size} class is applied by MUI
      // to the icon span, so we can target the SVG inside per size from within these slots.
      startIcon: {
        marginLeft: 0,
        marginRight: spacingTokens.sp4,
        // Target `svg` directly (not .MuiSvgIcon-root) so Phosphor icons are sized correctly.
        // Phosphor renders its own <svg> element without the MuiSvgIcon-root class.
        // width+height override Phosphor's default size="1em" attribute.
        '&.MuiButton-iconSizeMedium svg': { width: sizes.height.h16, height: sizes.height.h16 },
        '&.MuiButton-iconSizeLarge svg': { width: sizes.height.h20, height: sizes.height.h20 },
      },
      endIcon: {
        marginLeft: spacingTokens.sp4,
        marginRight: 0,
        '&.MuiButton-iconSizeMedium svg': { width: sizes.height.h16, height: sizes.height.h16 },
        '&.MuiButton-iconSizeLarge svg': { width: sizes.height.h20, height: sizes.height.h20 },
      },
    },
    variants: [
      {
        // Filter-trigger variant — shared shell for the Filter family (FilterDropdownButton +
        // MoreFiltersButton, src/components/Filter). Figma: FilterButtonDropdown (1264:4620) and
        // FiltersButtonMore (1264:4662). A neutral-bordered white pill, 36px, non-uppercase body1,
        // secondary text; hover/pressed use the same translucent dark overlay as contained Button.
        // Runtime modifiers set by the wrappers (data-* attributes on the button element):
        //   data-active="true"   → filters applied: primary-subtle fill + primary border and text
        //   data-open="true"     → rotates the trailing caret (dropdown open)
        // Icons inherit colour via currentColor, so the active/disabled text colour recolours them.
        props: { variant: 'filter' },
        style: {
          minWidth: 0,
          height: sizes.height.h36,
          borderStyle: 'solid',
          borderWidth: sizes.border.bd1,
          borderColor: intent.neutral.border,
          // rd-4 (designer, 2026-08-27) — was rd-2. This also brings the trigger in line with
          // `ToggleButton size="filter"`, whose root is already radius.sm: the two sit side by
          // side in a filter bar and used to disagree on their corners.
          borderRadius: radius.sm,
          backgroundColor: surface.white,
          color: text.secondary,
          textTransform: 'none',
          ...typography.body1,
          paddingTop: 0,
          paddingBottom: 0,
          // Horizontal insets are the SUM of Figma's two nested boxes — the component root and
          // the "Button Text" frame inside it — because MUI's Button has no text wrapper: its
          // children are direct flex items. Verified against Figma 2026-08-27.
          //
          // Dropdown / date trigger (no startIcon): root `sp-8` + text box `sp-4` = sp12 left.
          // On the right the text box's `sp-4` becomes the endIcon's own sp4 marginLeft (the
          // label→caret gap), leaving the root's sp-4 after the caret box.
          paddingLeft: spacingTokens.sp12,
          paddingRight: spacingTokens.sp4,

          // Hover / pressed — translucent dark overlay layered ON TOP of the current fill
          // (white by default, or primary-subtle when active), so the base token is preserved.
          // :active after :hover (equal specificity — last wins) gives the deeper pressed tint.
          '&:not(.Mui-disabled):hover': {
            backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
          },
          '&:not(.Mui-disabled):active': {
            backgroundImage: `linear-gradient(${state.pressed}, ${state.pressed})`,
          },

          // Active (filters applied) — primary subtle fill + primary border + text (icons follow
          // via currentColor). Applies to EVERY filter trigger: until 2026-08-27 the dropdown
          // stayed white here and only More Filters filled, opted in via a `data-fill` attribute.
          // Figma unified them, so the attribute is gone and there is one active look.
          '&[data-active="true"]': {
            backgroundColor: intent.primary.subtle,
            borderColor: intent.primary.border,
            color: intent.primary.text,
          },

          // Trailing caret (FilterDropdownButton only — MoreFiltersButton uses a startIcon).
          // Figma "Button Icon" (node 1459:6197): an h28 square container centering an h16 caret,
          // sat 4px (endIcon marginLeft, set in the shared endIcon slot) after the badge slot.
          // The container gives the caret consistent breathing room; no fill/border of its own.
          // The rotate-on-open transition lives here too.
          '& .MuiButton-endIcon': {
            width: sizes.height.h28,
            height: sizes.height.h28,
            // Never give up width when the trigger hits its max: a squashed caret reads as a
            // rendering bug, where a truncated value reads as intentional.
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 150ms ease',
            '& svg': { width: sizes.height.h16, height: sizes.height.h16 },
          },
          '&[data-open="true"] .MuiButton-endIcon': {
            transform: 'rotate(180deg)',
          },

          // Leading icon (MoreFiltersButton only — FilterDropdownButton has no startIcon). Same
          // Figma "Button Icon": an h28 square container centering an h16 glyph (the Funnel).
          '& .MuiButton-startIcon': {
            width: sizes.height.h28,
            height: sizes.height.h28,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { width: sizes.height.h16, height: sizes.height.h16 },
          },
          // MoreFiltersButton (leading icon, no caret). Figma gives its root `px-4` — a smaller
          // left inset than the dropdown's, because the h28 icon container already carries 6px of
          // visual air around its 16px glyph. The nested text box's left `sp-4` is supplied by the
          // startIcon's own marginRight.
          '&:has(.MuiButton-startIcon)': {
            paddingLeft: spacingTokens.sp4,
            paddingRight: spacingTokens.sp4,
          },
          // The TRAILING inset depends on whether a badge is there (Figma: the text box is
          // `pr-6` bare, `pr-4` + `gap-6` with a badge) — so 6+4=10 after a bare label, 4+4=8
          // after a badge. Carried as a margin on whichever element ends the row, rather than as
          // one padding on the root: 10 is not a spacing token, and inlining it would put a raw
          // value in the theme and break the mirror. Each half stays a single token instead.
          '&:has(.MuiButton-startIcon):not(:has([data-slot="badge"])) [data-slot="label"]': {
            marginRight: spacingTokens.sp6,
          },
          '&:has(.MuiButton-startIcon) [data-slot="badge"]': {
            marginRight: spacingTokens.sp4,
          },

          '&.Mui-disabled': {
            backgroundColor: surface.white,
            borderColor: intent.neutral.border,
            color: text.disabled,
          },
        },
      },
    ],
  },
  MuiIconButton: {
    defaultProps: {
      // Figma ButtonIcon default: size="large" (36px container, 18px icon).
      size: 'large',
    },
    styleOverrides: {
      root: {
        // Figma: square with rd-4 corners — not MUI's default circle (border-radius: 50%).
        borderRadius: radius.sm,
        // Exact pixel dimensions set per size below; padding 0 lets width/height dominate.
        padding: 0,

        // ── Text variant (default: data-variant="text") ──────────────────────────────
        // Hover/pressed use neutral dark overlays — same as text Button.
        // color prop drives icon colour via MUI's palette (primary → purple, error → red).
        '&:not([data-variant="contained"]):not(.Mui-disabled):hover': {
          backgroundColor: state.hover,
        },
        '&:not([data-variant="contained"]):not(.Mui-disabled):active': {
          backgroundColor: state.pressed,
        },
        '&:not([data-variant="contained"]).Mui-disabled': {
          color: text.disabled,
        },
        // Text secondary — a neutral/grey icon button (Figma 1182:7994). Unlike contained
        // secondary (navy fill), the TEXT variant's "secondary" recolours the icon to the neutral
        // `text.secondary` token. MUI's default for color="secondary" would be navy
        // (palette.secondary.main), so it is overridden here. Mirrors Button's textSecondary.
        '&:not([data-variant="contained"]).MuiIconButton-colorSecondary:not(.Mui-disabled)': {
          color: text.secondary,
        },

        // ── Contained variant (data-variant="contained") ─────────────────────────────
        // Solid background fill; hover/pressed use the overlay approach (same as Button).
        '&[data-variant="contained"]': {
          color: text.white,

          '&.MuiIconButton-colorPrimary': {
            backgroundColor: intent.primary.solid,
            '&:not(.Mui-disabled):hover': {
              backgroundColor: intent.primary.solid,
              backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
            },
            '&:not(.Mui-disabled):active': {
              backgroundImage: `linear-gradient(${state.pressed}, ${state.pressed})`,
            },
          },
          '&.MuiIconButton-colorSecondary': {
            backgroundColor: intent.secondary.solid,
            '&:not(.Mui-disabled):hover': {
              backgroundColor: intent.secondary.solid,
              backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
            },
            '&:not(.Mui-disabled):active': {
              backgroundImage: `linear-gradient(${state.pressed}, ${state.pressed})`,
            },
          },
          '&.MuiIconButton-colorError': {
            backgroundColor: intent.error.solid,
            '&:not(.Mui-disabled):hover': {
              backgroundColor: intent.error.solid,
              backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
            },
            '&:not(.Mui-disabled):active': {
              backgroundImage: `linear-gradient(${state.pressed}, ${state.pressed})`,
            },
          },
          '&.Mui-disabled': {
            backgroundColor: intent.neutral.disabled,
            color: text.disabled,
          },
        },
      },

      // Figma sizes: small=22px/14px icon, medium=28px/16px icon, large=36px/18px icon.
      // `svg` selector (not .MuiSvgIcon-root) — Phosphor renders its own <svg> without MUI's class.
      sizeSmall: {
        width: sizes.height.h22,
        height: sizes.height.h22,
        '& svg': { width: sizes.height.h14, height: sizes.height.h14 },
      },
      sizeMedium: {
        width: sizes.height.h28,
        height: sizes.height.h28,
        '& svg': { width: sizes.height.h16, height: sizes.height.h16 },
      },
      sizeLarge: {
        width: sizes.height.h36,
        height: sizes.height.h36,
        '& svg': { width: sizes.height.h18, height: sizes.height.h18 },
      },
    },
  },
  MuiFormControlLabel: {
    // SHARED: affects Checkbox, Radio, and Switch labels.
    // Approved globally — Figma uses body2 + sp4 gap for all selection-control labels.
    defaultProps: {
      slotProps: {
        typography: { variant: 'body2' },
      },
    },
    styleOverrides: {
      root: {
        // Gap between the control (radio/checkbox/switch) and its label text.
        gap: spacingTokens.sp4,
        // Reset MUI's default margins: MUI sets marginLeft: -8~-11px (to compensate for its
        // own touch-target padding) and marginRight: 16px (for row-layout spacing). Both
        // conflict with our gap-based spacing on RadioGroup/FormGroup. Spacing between items
        // is handled exclusively by MuiRadioGroup/MuiFormGroup gap overrides.
        // Blast radius: Checkbox, Radio, Switch labels — all use FormControlLabel.
        marginLeft: 0,
        marginRight: 0,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      // Base (unselected, enabled): neutral subtle fill + neutral border + secondary text.
      // Figma (node 913:2553) — selected is a single subtle+border+coloured-text treatment
      // (`intent.primary.*`) regardless of the `color` prop; there is no solid-fill selected
      // state in the current spec, so `color="standard"` no longer produces a distinct
      // selected look. Approved 2026-07-06 (see docs/DECISIONS.md).
      root: {
        backgroundColor: intent.neutral.subtle,
        borderColor: intent.neutral.border,
        borderWidth: sizes.border.bd1,
        borderRadius: radius.sm,
        color: text.secondary,

        '&:not(.Mui-selected):not(.Mui-disabled):hover': {
          backgroundColor: state.hover,
          borderColor: intent.neutral.border,
        },

        '&.Mui-selected': {
          // Figma (913:2553 / 916:3055): selected always keeps a full 1px border on every
          // side, even between two grouped buttons. MUI's default grouped CSS overlaps
          // adjacent borders with a negative margin so a neighbour's border can paint over
          // (and hide) this button's edge — `position: relative` + `zIndex` makes this
          // button's own border win regardless of DOM order.
          position: 'relative',
          zIndex: 1,
          backgroundColor: intent.primary.subtle,
          borderColor: intent.primary.border,
          borderWidth: sizes.border.bd1,
          color: intent.primary.text,
          '&:hover': {
            backgroundColor: intent.primary.subtle,
          },
        },

        '&.Mui-disabled': {
          backgroundColor: intent.neutral.disabled,
          borderColor: intent.neutral.border,
          color: text.disabled,
        },

        // Selected + disabled — faded primary border (`intent.primary.disabled`), fill stays
        // `intent.primary.subtle`, text drops to `text.disabled` (Figma node 1292:5056/58/62).
        '&.Mui-disabled.Mui-selected': {
          backgroundColor: intent.primary.subtle,
          borderColor: intent.primary.disabled,
          color: text.disabled,
        },

        // ── SelectionChip ──────────────────────────────────────────────────────
        // Selectable filter/token pill (Figma "Check Token", node 1537:5960), built on
        // ToggleButton. All rules are scoped to `.SelectionChip-root` so the default
        // ToggleButton look above is untouched. `select=single/multiselect` (Figma) maps to
        // the group's `exclusive`; multiselect adds a leading round checkbox
        // (`.SelectionChip-hasCheckbox`). Sizes: small (h28 / body2), medium (h36 / body1);
        // selected label is semibold. See docs/DECISIONS.md 2026-07-19.
        '&.SelectionChip-root': {
          // Pill shell, sizes, indicator and slots come from the shared recipe in
          // components/SelectionChip/selectionChipStyles.ts — menu-mode chips are a styled div
          // outside this theme's reach and consume the same values, so the two stay in step.
          ...chipShell,
          textTransform: 'none',

          '&.MuiToggleButton-sizeSmall': {
            ...chipSize.small,
            padding: `0 ${spacingTokens.sp12}px`,
          },
          '&.MuiToggleButton-sizeMedium': {
            ...chipSize.medium,
            padding: `0 ${spacingTokens.sp12}px`,
          },
          // Checkbox present → tighter left padding (sp2) so the round indicator sits close to the
          // edge; right stays sp12. Both sizes share the same padding (Figma node 1537:5960).
          '&.SelectionChip-hasCheckbox': {
            padding: `0 ${spacingTokens.sp12}px 0 ${spacingTokens.sp2}px`,
          },

          // Loading — a trailing spinner in the slot Figma reserves for the caret ("Button Icon"
          // is hidden while `loader` shows, node 2552:598). Figma splits the trailing space as
          // label → sp4 → 12px ring → sp8 → edge, so the pill's right padding drops from sp12 to
          // sp8 and the label gets its own sp4 inset back. Must stay AFTER `-hasCheckbox`: both
          // rules have equal specificity, so source order decides the right padding.
          //
          // Layout only. `loading` implies `disabled` (as in MUI's Button), so the greyed look
          // and the blocked interaction both come from `Mui-disabled` above.
          '&.SelectionChip-loading': {
            paddingRight: spacingTokens.sp8,
            '& .SelectionChip-label': { paddingRight: CHIP_LABEL_INSET },
          },
          '& .SelectionChip-loader': chipLoaderSlot,

          // Leading icon slot (Figma "showIcon"): 18px (small) / 20px (medium). Unchecked, its
          // colour is `text.secondary` (the label stays `text.primary`); selected/disabled follow
          // the label colour (see the state blocks below).
          '& .SelectionChip-icon': { ...chipIconSlot, color: text.secondary },
          '&.MuiToggleButton-sizeSmall .SelectionChip-icon svg': chipIconSvgSize.small,
          '&.MuiToggleButton-sizeMedium .SelectionChip-icon svg': chipIconSvgSize.medium,

          // Label sits in its own "text container" with a small left inset — ONLY when a leading
          // checkbox or icon precedes it. A bare chip (single-select, no icon) has no inset so its
          // label stays centred (Figma node 1537:5960).
          '&.SelectionChip-hasCheckbox .SelectionChip-label, &.SelectionChip-hasIcon .SelectionChip-label': {
            paddingLeft: CHIP_LABEL_INSET,
          },

          // Selected — subtle purple fill, primary border, semibold primary label; icon matches.
          '&.Mui-selected': {
            ...chipSelected,
            '&:hover': { backgroundColor: intent.primary.subtle },
            '& .SelectionChip-icon': { color: intent.primary.solid },
          },

          // Disabled unchecked — flat grey fill + same-hue border, disabled text + icon.
          '&.Mui-disabled': {
            ...chipDisabled,
            '& .SelectionChip-icon': { color: text.disabled },
          },
          // Disabled selected — keeps the subtle purple fill, drops the border, disabled text + icon.
          '&.Mui-disabled.Mui-selected': {
            ...chipDisabledSelected,
            '& .SelectionChip-icon': { color: text.disabled },
          },

          // Leading round checkbox indicator (multiselect). A 28px hit-area box centring an
          // 18px circle, mirroring the Figma Checkbox sub-component; the check shows on select.
          '& .SelectionChip-indicator': chipIndicatorBox,
          '& .SelectionChip-circle': chipCircle,
          // Unlike menu mode (which mounts the glyph conditionally), the plain chip always has
          // the check in the DOM and toggles its visibility.
          '& .SelectionChip-check': { ...chipGlyph, visibility: 'hidden' },
          '&.Mui-selected .SelectionChip-circle': chipCircleSelected,
          '&.Mui-selected .SelectionChip-check': {
            visibility: 'visible',
          },
          '&.Mui-disabled .SelectionChip-circle': chipCircleDisabled,
          '&.Mui-disabled.Mui-selected .SelectionChip-circle': chipCircleDisabledSelected,
        },
      },
      sizeSmall: {
        height: sizes.height.h28,
        gap: spacingTokens.sp8,
        padding: `0 ${spacingTokens.sp16}px`,
        ...typography.buttonSm,
      },
      sizeMedium: {
        height: sizes.height.h36,
        gap: spacingTokens.sp8,
        padding: `0 ${spacingTokens.sp16}px`,
        ...typography.button,
      },
    },
    variants: [
      {
        // Custom size (module augmentation in ToggleButton.tsx) — used for filter/toolbar
        // toggles (e.g. chart view switches). Not uppercase, unlike small/medium.
        props: { size: 'filter' },
        style: {
          height: sizes.height.h36,
          gap: spacingTokens.sp8,
          padding: `0 ${spacingTokens.sp16}px`,
          ...typography.captionBold,
          textTransform: 'none',
          '& svg': {
            width: sizes.height.h16,
            height: sizes.height.h16,
          },
        },
      },
    ],
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        borderRadius: radius.sm,

        // SelectionChipGroup — chips stay as separate rounded pills with a gap and wrap onto
        // multiple rows, rather than merging into one segmented control. The high-specificity
        // selector (`.MuiToggleButtonGroup-root.SelectionChipGroup-root .SelectionChip-root`)
        // resets MUI's default grouped border-merging (negative margins + squared inner
        // corners). See docs/DECISIONS.md 2026-07-19.
        '&.SelectionChipGroup-root': {
          flexWrap: 'wrap',
          gap: spacingTokens.sp8,
          '& .SelectionChip-root': {
            margin: 0,
            borderRadius: radius.pill,
          },
          // MUI drops the left border between two ADJACENT selected buttons (`.Mui-selected +
          // .Mui-selected`) so a merged segmented control doesn't double it. Our chips are
          // separate pills with a gap, so that leaves the second selected chip's left edge
          // open — restore it. The extra `.SelectionChip-root` classes make this outrank MUI's
          // rule deterministically (0,5,0 > 0,3,0), avoiding the order-dependent tie noted in
          // docs/DECISIONS.md 2026-07-07.
          //
          // The COLOUR must be restored explicitly (fixed 2026-08-13). MUI writes the shorthand
          // `borderLeft: 0`, which resets all three longhands — including `border-left-color`
          // back to `currentColor`. Restoring only width + style therefore left a 1px edge
          // painted in the chip's TEXT colour (`intent.primary.solid`), i.e. a hard dark arc
          // down the left side of the second selected chip, against the soft `*040` tint on its
          // other three sides.
          '& .SelectionChip-root.Mui-selected + .SelectionChip-root.Mui-selected': {
            borderLeftWidth: sizes.border.bd1,
            borderLeftStyle: 'solid',
            borderLeftColor: intent.primary.border,
            marginLeft: 0,
          },
          // Same pair, both disabled: the disabled+selected pill drops its border entirely
          // (transparent fill edge), so the restored left edge must match — otherwise the
          // rule above would give it a visible border the other three sides don't have.
          '& .SelectionChip-root.Mui-selected.Mui-disabled + .SelectionChip-root.Mui-selected.Mui-disabled': {
            borderLeftColor: 'transparent',
          },
        },
      },
      // Divider between grouped buttons — reuse neutral border colour.
      // `&.Mui-selected:not(:first-of-type)` (specificity 0,3,0) intentionally outranks
      // `&:not(:first-of-type)` (0,2,0) below so a selected middle/last button always keeps
      // its own primary border colour, regardless of emitted CSS order between this override
      // and `MuiToggleButton`'s root `.Mui-selected` rule (equal-specificity ties are order-
      // dependent and unreliable — see docs/DECISIONS.md, 2026-07-07).
      grouped: {
        '&:not(:first-of-type)': {
          borderColor: intent.neutral.border,
        },
        '&.Mui-disabled:not(:first-of-type)': {
          borderColor: intent.neutral.border,
        },
        '&.Mui-selected:not(:first-of-type)': {
          borderColor: intent.primary.border,
        },
        '&.Mui-disabled.Mui-selected:not(:first-of-type)': {
          borderColor: intent.primary.disabled,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        // Figma: px=sp12 (12px), py=sp2 (2px). MUI medium default = 16.5px 14px — override.
        // Root height is fixed at h36; the root flex container centres this smaller input
        // element vertically within the 36px shell.
        padding: `${spacingTokens.sp2}px ${spacingTokens.sp12}px`,
        '&::placeholder': {
          color: text.disabled,
          opacity: 1,
        },
        // Multiline: top padding is sp4, not sp2 (Figma spec for the textarea). The `multiline`
        // class only lands on the root, not the input/textarea itself, hence the ancestor form.
        '.MuiInputBase-multiline &': {
          paddingTop: `${spacingTokens.sp8}px`,
        },
      },
      notchedOutline: {
        // Default border: neutral — lighter than MUI default rgba(0,0,0,0.23).
        // Focused: palette.primary.main = purple — correct via theme palette, no override needed.
        borderColor: intent.neutral.border,
        // MUI's default top:-5px is intentionally preserved. It makes the fieldset extend
        // 5px above the root so the visible border sits inside the label tab's bg area,
        // creating the "label sits on input" visual connection. Setting top:0 breaks this.
        // Explicit border-radius: fieldset has border-radius:0 in some browsers by default;
        // MUI sets border-radius:inherit on the fieldset but some configurations need it explicit.
        borderRadius: `${radius.sm}px`,
      },
      root: {
        // White fill for all non-disabled states so the label tab (which extends slightly
        // under the input due to the fieldset's top:-5px offset) is not visible through
        // a transparent input background. Disabled overrides this below with its own fill.
        backgroundColor: surface.white,

        // Figma: h=36px for single-line. Multiline grows freely with content — no fixed height.
        // Autocomplete's inputRoot is excluded from the FIXED height: MUI expects that root to
        // grow when multi-select chips wrap onto more than one row (it has no fixed height of
        // its own, only its own padding). A fixed height clipped the box, making chips overflow
        // past the bottom border. `minHeight` keeps single-select/no-chip Autocomplete at exactly
        // 36px (unchanged) while letting multi-select grow to fit wrapped chips.
        '&:not(.MuiInputBase-multiline):not(.MuiAutocomplete-inputRoot)': {
          height: sizes.height.h36,
        },
        '&.MuiAutocomplete-inputRoot:not(.MuiInputBase-multiline)': {
          minHeight: sizes.height.h36,
        },

        // Multiline: MUI's own `multiline` variant puts 16.5px/14px padding on the ROOT (on
        // top of the padding already set on the `input` slot below), which doubles up the
        // padding vs single-line. Zero it out here so root+input padding matches single-line
        // exactly (the `input` slot's sp2/sp12 padding, set below, is the only padding).
        '&.MuiInputBase-multiline': {
          padding: 0,
        },

        // Leading adornment (e.g. a search icon or a "USD" prefix): drop the input's own left
        // padding so the text sits right after the adornment's 8px margin (icon→text = 8px, matching
        // the Sidebar search). Without this the input keeps its sp12 left padding on top of the 8px
        // adornment margin → a 20px icon→text gap (the text looks detached from the icon). Excludes
        // Autocomplete (its `.MuiAutocomplete-input` owns its own padding) and the multiline textarea.
        '&.MuiInputBase-adornedStart .MuiOutlinedInput-input:not(.MuiAutocomplete-input)': {
          paddingLeft: 0,
        },

        // Multiline: explicit border colour for the default state only. Scoped with the same
        // :not() exclusions as single-line so it doesn't out-specificity-tie (and win against)
        // MUI's own focused/error border rules — without these, clicking into (or erroring) a
        // multiline field kept the border neutral-grey instead of turning primary/error.
        '&.MuiInputBase-multiline:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error):not(.MuiInputBase-colorSuccess) .MuiOutlinedInput-notchedOutline':
          {
            borderColor: intent.neutral.border,
          },

        // Hover — color-mix overlay strategy: darken each variant's border by mixing in 4% black
        // via CSS color-mix(). 4% is the system-wide hover overlay (`state.hover` =
        // rgba(0,0,0,0.04)), so compositing it onto the border matches both Figma's hover border
        // (neutral #e9e7e6 → #e0dedd) and the hover tint used everywhere else (Button, MenuItem,
        // Table, list options, label tab). Stays exactly on the border (no shadow ring outside the
        // element). Three explicit selectors prevent MUI's default hover (which switches to
        // palette.text.primary / palette.error.dark etc.).

        // Default hover: neutral border darkened 4%. Excludes read-only — that state has no
        // hover border change (see read-only block below).
        '&:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error):not(.MuiInputBase-colorSuccess):not(.MuiInputBase-readOnly) .MuiOutlinedInput-notchedOutline':
          {
            borderColor: `color-mix(in srgb, ${intent.neutral.border}, black 4%)`,
          },

        // Error hover: red border darkened 4%.
        '&.Mui-error:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
          borderColor: `color-mix(in srgb, ${intent.error.border}, black 4%)`,
        },

        // Positive / success (color="success"): always show intent.success.border in every
        // non-hover, non-disabled state. The focused selector has higher specificity than
        // MUI's .Mui-focused override (which would switch to palette.success.main).
        '&.MuiInputBase-colorSuccess .MuiOutlinedInput-notchedOutline': {
          borderColor: intent.success.border,
        },
        '&.MuiInputBase-colorSuccess.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: intent.success.border,
        },
        '&.MuiInputBase-colorSuccess:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
          borderColor: intent.success.border,
        },
        // Success hover: green border darkened 4%.
        '&.MuiInputBase-colorSuccess:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
          borderColor: `color-mix(in srgb, ${intent.success.border}, black 4%)`,
        },

        // Error: value text turns red (Figma: intent/error/text).
        '&.Mui-error .MuiInputBase-input': {
          color: intent.error.text,
        },

        // Read-only: completely borderless (Figma: no border, no fill) + no horizontal padding.
        // Figma read-only input has px=none (0px) — no left/right padding on the input.
        // Scoped via the `.MuiInputBase-readOnly` class InputBase puts on the root (driven by
        // ownerState.readOnly) rather than `:has([readonly])` — the latter also matches
        // multiline's hidden TextareaAutosize sizing clone, which MUI always renders with a
        // `readonly` attribute, incorrectly flagging every multiline field as read-only.
        '&.MuiInputBase-readOnly .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '&.MuiInputBase-readOnly .MuiOutlinedInput-input': {
          paddingLeft: 0,
          paddingRight: 0,
          cursor: 'default',
        },
        // No hover border change (handled by :not(.MuiInputBase-readOnly) above) and no cursor
        // change on hover — InputBase's own base style sets `cursor: text` on the ROOT (not just
        // the input), so the root itself must also be overridden or the row above/below the
        // input still shows a text-edit cursor even though the field can't be edited.
        '&.MuiInputBase-readOnly': {
          cursor: 'default',
          // Read-only is borderless AND fill-less (Figma: no border, no fill). The root's
          // base style sets a white fill for every non-disabled state to hide the label tab
          // behind the input; read-only has no visible border/tab, so the fill would just
          // paint a white box on any non-white surface — override it back to transparent.
          backgroundColor: 'transparent',
        },

        // Disabled: grayed fill + disabled border.
        '&.Mui-disabled': {
          backgroundColor: intent.neutral.disabled,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: state.disabled.border,
        },
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: surface.sunken,
        '&.Mui-disabled': {
          backgroundColor: intent.neutral.disabled,
        },
      },
    },
  },
  // MuiInput (standard variant) and MuiInputLabel require no overrides:
  // - InputLabel colors derive from palette.text.secondary / palette.primary.main / palette.error.main
  //   which are already mapped to our text.secondary / intent.primary.text / intent.error.text tokens.
  // - Input focus underline uses palette.primary.main = purple. Already correct.
  // - TextField wrapper (src/components/TextField/TextField.tsx) renders no MUI InputLabel;
  //   the label tab is a custom div above the input.
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        // Custom body variants render as <p>, matching MUI's body1/body2 behaviour.
        // Other custom variants (buttonSm, captionBold, captionSm, overlineBold) default to <span> — correct.
        body1Bold: 'p',
        body2Bold: 'p',
        // Display is hero text, not a heading — no implicit document structure.
        display: 'p',
        displaySubtitle: 'p',
      },
    },
  },
  MuiAvatar: {
    // Figma node 1455:11018. The `Avatar` wrapper (src/components/Avatar/Avatar.tsx) maps its
    // `size` and `color` props to `data-size` / `data-color` attributes on the root; everything
    // below is keyed off them (same pattern as IconButton's data-variant). All token-based.
    defaultProps: {
      // Phosphor-only icon rule (docs/ICONS.md): replace MUI's internal Material `Person`
      // fallback SVG with the brand Phosphor `User`. Same approved-exception pattern as
      // Chip.deleteIcon / Autocomplete popup+clear icons.
      slots: { fallback: AvatarFallback },
    },
    styleOverrides: {
      root: {
        // Sizes (Figma Large/Default/Small). Label typography is size-dependent: Large = body1Bold
        // (14/20 semibold), Default & Small = body2 (12/18 regular).
        '&[data-size="large"]': {
          width: sizes.height.h36,
          height: sizes.height.h36,
          fontSize: typography.body1Bold.fontSize,
          lineHeight: typography.body1Bold.lineHeight,
          fontWeight: typography.body1Bold.fontWeight,
        },
        '&[data-size="medium"]': {
          width: sizes.height.h24,
          height: sizes.height.h24,
          fontSize: typography.body2.fontSize,
          lineHeight: typography.body2.lineHeight,
          fontWeight: typography.body2.fontWeight,
        },
        '&[data-size="small"]': {
          width: sizes.height.h16,
          height: sizes.height.h16,
          fontSize: typography.body2.fontSize,
          lineHeight: typography.body2.lineHeight,
          fontWeight: typography.body2.fontWeight,
        },
        // Colours — letter/icon avatars only. When a `src` is set, MUI renders the <img> which
        // covers the fill, so these are visually inert for image avatars. The attribute selector
        // (0,2,0) outranks MUI's own `.MuiAvatar-colorDefault` (0,1,0), so our fill always wins.
        // AvatarGroup's "+N" surplus avatar inherits data-color="neutral" from the wrapper default.
        '&[data-color="neutral"]': {
          backgroundColor: intent.neutral.solid,
          color: intent.neutral.onSolid,
        },
        '&[data-color="primary"]': {
          backgroundColor: intent.primary.solid,
          color: intent.primary.onSolid,
        },
        '&[data-color="secondary"]': {
          backgroundColor: intent.secondary.solid,
          color: intent.secondary.onSolid,
        },
      },
    },
  },
  MuiChip: {
    // Figma node 802:2408. Filled only — Figma defines no outlined variant (designer: not needed
    // for now, chips here are non-interactive, so there is no hover/focus spec either). Two sizes
    // (small/medium) × five colours (`default` + success/error/warning/info) × default/disabled.
    // The Figma statusDot slot is covered by the standalone `StatusDot` component hosted in the
    // Chip `icon` slot (see StatusDot.usage.md).
    defaultProps: {
      // The removable chip's trailing affordance (Figma suffixSymbol) maps to MUI's deleteIcon
      // slot. MUI's internal Cancel icon is replaced with the brand Phosphor XCircle — same
      // Phosphor-only exception approved for Autocomplete's popup/clear icons (see docs/DECISIONS.md).
      deleteIcon: createElement(XCircle),
    },
    styleOverrides: {
      root: {
        // `color="default"` fill. Figma binds `Intent/Neutral/Subtle` (= grey006) — same value as
        // the `Surface/Sunken` it used to bind, but on the intent scale, so all five colours now
        // read as one formula: fill `intent.<c>.subtle`, label `intent.<c>.text`.
        backgroundColor: intent.neutral.subtle,
        borderRadius: radius.pill,
        // Figma: root horizontal padding = sp6; the label wrapper adds sp4 (see `label` below),
        // so a bare chip has 10px text inset. Icons/avatar/deleteIcon sit at sp6 from the edge.
        paddingLeft: spacingTokens.sp6,
        paddingRight: spacingTokens.sp6,
        // Two sizes only — medium h28, small h22 (replaces MUI's 32/24 defaults).
        '&.MuiChip-sizeMedium': { height: sizes.height.h28 },
        '&.MuiChip-sizeSmall': { height: sizes.height.h22 },
        // Label is size-dependent in Figma: medium = body1 (14/20) + sp6 side padding,
        // small = body2 (12/18) + sp4 side padding (the label-wrapper inset around the text).
        '&.MuiChip-sizeMedium .MuiChip-label': {
          fontSize: typography.body1.fontSize,
          lineHeight: typography.body1.lineHeight,
          paddingLeft: spacingTokens.sp6,
          paddingRight: spacingTokens.sp6,
        },
        '&.MuiChip-sizeSmall .MuiChip-label': {
          fontSize: typography.body2.fontSize,
          lineHeight: typography.body2.lineHeight,
          paddingLeft: spacingTokens.sp4,
          paddingRight: spacingTokens.sp4,
        },
        // Disabled (Figma): fill stays at full strength; MUI's blanket 0.38 fade on the whole chip
        // is cancelled so the label recolours to text.disabled and only the decorations dim.
        // Deliberately does NOT restate `backgroundColor` — MUI's disabled style only touches
        // opacity, so leaving the fill alone lets each colour variant's own subtle fill survive.
        // Pinning it here (it used to say `surface.sunken`) greyed out every coloured chip, because
        // `.MuiChip-root.Mui-disabled` (0,2,0) outranks a variant's root-level fill (0,1,0).
        '&.Mui-disabled': {
          opacity: 1,
        },
        '&.Mui-disabled .MuiChip-label': { color: text.disabled },
        // Decorations dim to the disabled-content alpha — matches Figma's opacity-38 on the
        // avatar / prefix icon / delete icon layers when disabled.
        '&.Mui-disabled .MuiChip-avatar, &.Mui-disabled .MuiChip-icon, &.Mui-disabled .MuiChip-deleteIcon': {
          opacity: stateAlpha.disabledContent,
        },
      },
      label: {
        // fontSize/lineHeight AND side padding are set per size on the root (medium sp6, small sp4).
        fontWeight: typography.body2.fontWeight,
        color: text.secondary,
      },
      // Prefix icon (MUI `icon` slot) = Figma prefixSymbol, 16×16. Spacing comes from root sp6
      // (edge) + label sp4 (gap), so MUI's default icon margins are zeroed.
      icon: {
        width: sizes.height.h16,
        height: sizes.height.h16,
        margin: 0,
        color: text.secondary,
      },
      // Avatar (MUI `avatar` slot) — always 16×16 with a neutral fill (Figma chip spec 802:2408),
      // regardless of the size/color props on a passed <Avatar>. Equivalent to Avatar
      // size="small" (body2) + color="neutral".
      // `&&&` (0,3,0) reliably outranks the Avatar wrapper's own `[data-size]`/`[data-color]`
      // rules (both 0,2,0) — their CSS injection order vs MuiChip is not something we control, so
      // a plain `&&` (0,2,0) tie would let a default-size (36px) avatar leak through and overflow
      // the chip (see the equal-specificity lesson in DECISIONS 2026-07-07, ToggleButton).
      avatar: {
        // Match BOTH classes the avatar element carries (`.MuiChip-avatar.MuiAvatar-root`) so the
        // rule is (0,3,0) and reliably beats the Avatar wrapper's own `[data-size]`/`[data-color]`
        // rules (0,2,0) regardless of CSS injection order — a plain `&&` (0,2,0) tie lets a
        // default-size (36px) avatar leak through and overflow the chip (verified via Playwright).
        '&.MuiAvatar-root': {
          width: sizes.height.h16,
          height: sizes.height.h16,
          margin: 0,
          fontSize: typography.body2.fontSize,
          lineHeight: typography.body2.lineHeight,
          fontWeight: typography.body2.fontWeight,
          backgroundColor: intent.neutral.solid,
          color: intent.neutral.onSolid,
        },
      },
      deleteIcon: {
        width: sizes.height.h16,
        height: sizes.height.h16,
        margin: 0,
        color: text.secondary,
        '&:hover': {
          color: text.primary,
        },
      },
    },
    // Colour variants (Figma `Color`, node 802:2408 — 28 variants: 7 colours × 2 sizes × enabled/
    // disabled). One formula: fill `intent.<c>.subtle`, and label + both icons `intent.<c>.text`.
    // `color="default"` is the neutral base in `root` above, so it needs no variant.
    //
    // Every rule here has to be explicit, because MUI's own colour variants fight us:
    //   - they set a SOLID `palette[color].main` fill and `contrastText` text (we want subtle+tint),
    //   - and a separate `iconColor === color` variant sets `.MuiChip-icon { color: inherit }`,
    //     which would pull the icon to that white `contrastText`.
    // Ours win because theme variants are emitted after the component's internal ones, so equal
    // specificity resolves in source order.
    //
    // Disabled needs nothing per colour: the fill stays (see `&.Mui-disabled` above), the label is
    // forced to `text.disabled` by a (0,3,0) rule that outranks these, and the icons keep their
    // tint at 38% opacity — exactly Figma's disabled treatment.
    //
    // NOTE (a11y, tracked in docs/DECISIONS.md): label-on-fill contrast is below WCAG AA for
    // success (3.33:1), error (3.64:1) and warning (1.73:1). Kept 1:1 with Figma at the designer's
    // request — they will revisit the palette once all real usages are collected. Do not "fix" the
    // colours here; the change belongs in the tokens. `primary` (5.99:1) and `secondary` (14.64:1)
    // are fine, as are `info` (6.15:1) and `default` (5.62:1).
    variants: (['primary', 'secondary', 'success', 'error', 'warning', 'info'] as const).map(color => ({
      props: { color },
      style: {
        backgroundColor: intent[color].subtle,
        '& .MuiChip-label': { color: intent[color].text },
        // The prefix icon (Figma prefixSymbol) and the delete icon (suffixSymbol) both follow the
        // label. Hover is pinned to the same colour: these chips are non-interactive by design, and
        // Figma specs no hover — without it, the `deleteIcon` slot's `text.primary` hover would
        // apply (equal specificity, so injection order would silently decide the colour).
        '& .MuiChip-icon': { color: intent[color].text },
        '& .MuiChip-deleteIcon': {
          color: intent[color].text,
          '&:hover': { color: intent[color].text },
        },
        // The avatar stays neutral grey on every colour (Figma binds `Intent/Neutral/Solid` even on
        // a coloured chip — confirmed intentional: an avatar is a person, not a status).
        // Worth knowing: MUI ships its OWN `color: primary` / `color: secondary` variants whose
        // only job is to repaint the avatar to `palette.<c>.dark`. The `avatar` slot override above
        // beats them, so nothing is needed here — verified in the browser for all 7 colours
        // (2026-08-12). If an avatar ever turns purple/navy on a coloured chip, that is what came
        // back, and the fix is to pin `& .MuiChip-avatar` in these variants.
      },
    })),
  },
  MuiAutocomplete: {
    // Underlying input styling (label tab, border, states) is inherited from MuiOutlinedInput —
    // Figma's Autocomplete node (1222-5673) is pixel-identical to TextField's (668-1210): same 8
    // states, same tokens, and the same h36 field height. Only the popup/clear icons, the dropdown
    // listbox, and the input-box geometry fix below are Autocomplete-specific. The listbox is a
    // token-based best-effort default (not in the Figma frame), approved by the designer pending a
    // dedicated spec for the open/listbox state.
    defaultProps: {
      // MUI's defaults (ArrowDropDown / Close) are internal, non-Phosphor icons — replaced per
      // the Phosphor-only icon rule (docs/ICONS.md). Approved exception recorded in DECISIONS.md.
      popupIcon: createElement(CaretDown),
      clearIcon: createElement(X),
      slotProps: {
        // Global MuiIconButton default is size="large" (36px) — far too big inside a 36px input.
        // Scoped to Autocomplete's indicators only; does not touch the global IconButton default.
        popupIndicator: { size: 'small' },
        clearIndicator: { size: 'small' },
        // Multi-select tags: Figma (node 1222-5673, `chip` inside the field) shows the small Chip
        // (h22), not medium (h28). MUI's default renderTags sizes chips from the Autocomplete's
        // own `size` prop, which is medium here — so force small via the `chip` slot.
        chip: { size: 'small' },
      },
    },
    styleOverrides: {
      inputRoot: {
        // Match TextField's box exactly for the single-line combobox (Figma 1222-5673: h36,
        // pixel-identical to TextField). MUI's Autocomplete adds `padding: 9px` to the
        // OutlinedInput root and `7.5px 4px 7.5px 5px` to `.MuiAutocomplete-input` (see `input`
        // below), which together push the field to ~53px. Zero the ROOT's vertical padding and
        // align its left inset to sp12 so a single value sits at h36 like TextField. The h36 floor
        // itself comes from `minHeight` on MuiOutlinedInput (`.MuiAutocomplete-inputRoot`), left as
        // a floor — not a fixed height — so multi-select chips can still wrap and grow past 36.
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: spacingTokens.sp12,
        // Kill MUI's `7.5px 4px 7.5px 5px` on `.MuiAutocomplete-input` and use TextField's sp2/0
        // so the text baseline lines up with a plain TextField (root supplies the sp12 left inset
        // above; the right side is reserved for the popup/clear icons). Nested here — not in the
        // `input` slot — because MUI applies the padding via a descendant selector
        // (`.MuiAutocomplete-inputRoot .MuiAutocomplete-input`, specificity 0,2,0) that a bare
        // `.MuiAutocomplete-input` slot override (0,1,0) would lose to.
        '& .MuiAutocomplete-input': {
          paddingTop: spacingTokens.sp2,
          paddingBottom: spacingTokens.sp2,
          paddingLeft: 0,
        },
        // Read-only (Figma 1222-5814): the field is borderless, fill-less AND has px=none. The
        // border/fill removal comes from MuiOutlinedInput's `.MuiInputBase-readOnly` rules; here we
        // also drop the sp12 left inset the combobox normally carries so read-only text is flush.
        '&.MuiInputBase-readOnly': {
          paddingLeft: 0,
          // No dropdown arrow in read-only — the list can't be opened, and Figma's read-only field
          // shows no popup indicator. Hide it (clear indicator is already suppressed by MUI when
          // readOnly). Scoped to Autocomplete's own read-only root, so no other component is hit.
          '& .MuiAutocomplete-popupIndicator': {
            display: 'none',
          },
        },
      },
      endAdornment: {
        // Align with the input's sp12 horizontal padding (MUI default is 9px).
        right: spacingTokens.sp12,
      },
      popupIndicator: {
        color: text.secondary,
      },
      clearIndicator: {
        color: text.secondary,
      },
      paper: {
        // The open list is styled 1:1 with the Menu component (Figma Menu page 1462:264):
        // same surface (white fill, 1px neutral border, rd-4) as `MuiMenu.paper`. Scoped to
        // Autocomplete's own popup Paper via the `paper` slot — does NOT touch the global MuiPaper
        // override (SHARED by Card, Dialog, Menu, Drawer, Popover, Accordion). marginTop keeps the
        // sp4 gap from the input (Autocomplete-specific; Menu anchors flush to its trigger).
        backgroundColor: surface.white,
        marginTop: spacingTokens.sp4,
        borderRadius: radius.sm,
        border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
        // Overlay elevation (Figma elevation/8) so it matches Menu/Select dropdowns. MUI's
        // Autocomplete Paper defaults to elevation 1 — bumped here for a consistent dropdown look.
        boxShadow: shadowTokens.elevation8,
      },
      listbox: ({ theme }) => ({
        // List + option styling mirror the Menu component 1:1 (MenuList + MenuItem overrides).
        // MenuList vertical padding = sp8.
        padding: `${spacingTokens.sp8}px 0`,
        // Options are written as a descendant selector (`.MuiAutocomplete-listbox
        // .MuiAutocomplete-option`, specificity 0,2,0) because MUI styles the option at the same
        // specificity — a bare `option` slot (0,1,0) loses and MUI's defaults render instead.
        '& .MuiAutocomplete-option': {
          // Row metrics identical to MenuItem: body1 (14/20), h36 rows, sp16 side padding, sp8 gap.
          ...typography.body1,
          minHeight: sizes.height.h36,
          // MUI sets the option's `minHeight: auto` at the `sm` breakpoint (same as MenuItem),
          // which collapses rows to text height on desktop — re-assert h36 there.
          [theme.breakpoints.up('sm')]: {
            minHeight: sizes.height.h36,
          },
          paddingLeft: spacingTokens.sp16,
          paddingRight: spacingTokens.sp16,
          paddingTop: 0,
          paddingBottom: 0,
          gap: spacingTokens.sp8,
          color: text.primary,
          // Hover / keyboard highlight both land on `.Mui-focused` in Autocomplete — tint with
          // state.hover, matching MenuItem's hover overlay.
          '&.Mui-focused': {
            backgroundColor: state.hover,
          },
          // Selected value: primary.subtle bg + primary.text, identical to MenuItem's
          // `.Mui-selected` (and Checkbox/Radio/Table selection). `aria-selected` is
          // Autocomplete's selected marker (MenuItem uses `.Mui-selected`).
          '&[aria-selected="true"]': {
            backgroundColor: intent.primary.subtle,
            color: intent.primary.text,
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: intent.primary.subtle,
          },
          // Disabled option: fixed text.disabled colour (project convention, not an opacity fade)
          // — same as MenuItem disabled.
          '&[aria-disabled="true"]': {
            color: text.disabled,
            opacity: 1,
          },
        },
      }),
      groupLabel: {
        color: text.secondary,
        fontSize: typography.caption.fontSize,
      },
      noOptions: {
        color: text.secondary,
        fontSize: typography.body2.fontSize,
      },
      loading: {
        color: text.secondary,
        fontSize: typography.body2.fontSize,
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        // Table surfaces are explicitly white (Figma: base/white). Kept as an explicit slot
        // override even though `background.paper` = surface.white (2026-07-30) so the intent is
        // self-documenting and survives any future change to the global Paper default.
        // Scoped to the Paper-backed case only (`<TableContainer component={Paper}>`): the
        // `.MuiPaper-root` compound selector (specificity 0,2,0) reliably beats MuiPaper's
        // own background (0,1,0). Plain-div TableContainers are left transparent (untouched).
        '&.MuiPaper-root': {
          backgroundColor: surface.white,
        },
        // rd-8 table corners, set here since the global MuiPaper radius was removed (2026-10-24).
        // `.MuiPaper-rounded` keeps `square` Paper-backed tables sharp. This only covers
        // `TableContainer component={Paper}`: a SEPARATE outer Paper around a table (e.g. table +
        // pagination in one card) sets `borderRadius: 2` (rd-8) itself.
        '&.MuiPaper-rounded': {
          borderRadius: radius.md,
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        // Selected row uses the brand primary-subtle tint (same selection pattern as
        // Checkbox / Radio / Switch / ToggleButton), NOT MUI's neutral `action.selected`.
        // Figma node 1382:4805 (TableRow, State=Selected → intent/primary/subtle).
        '&.Mui-selected': {
          backgroundColor: intent.primary.subtle,
        },
        /*
         * Selected AND hovered: the selection tint with the ordinary hover overlay ON TOP
         * (designer, 2026-09-25). A selected row is still a row the pointer can be over, and
         * swallowing its hover made it the one row in the table that did not answer the pointer.
         *
         * The overlay goes on as a `background-image` rather than as a mixed colour, because
         * `state.hover` is translucent — the same layering the contained IconButton uses. The
         * `backgroundColor` is restated because MUI's own `.MuiTableRow-hover:hover` rule would
         * otherwise replace the tint with `action.hover`.
         *
         * NOTE: this diverges from Figma 1382:4805, which draws no separate selected+hover state.
         */
        '&.Mui-selected:hover': {
          backgroundColor: intent.primary.subtle,
          backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        // Cell divider colour. MUI derives its own `palette.TableCell.border` as
        // `lighten(divider, 0.88)` — a formula tuned for MUI's default translucent-black
        // divider (rgba(0,0,0,0.12) → ~#e0e0e0). Our `divider` is already a light grey
        // (`intent.neutral.border` = #e9e7e6), so lightening it 88% yields ~#fcfcfc — a border
        // that's invisible on the near-white paper. Force the intended token colour instead.
        // Figma cells use `intent/neutral/border` directly (no lightening).
        borderBottomColor: intent.neutral.border,
        /*
         * Body cells are body1 (14 / 20), not MUI's default body2 (designer, 2026-10-06). Applies to
         * every table in the system — `Table`, `DataTable` and the `TableView` template — which is
         * the point: one reading size for tabular data. SHARED within the Table family only; no
         * other component renders a `TableCell`.
         */
        fontSize: typography.body1.fontSize,
        lineHeight: typography.body1.lineHeight,
      },
      // Header cells are body1Bold (SemiBold 600, 14 / 20) — the same metrics as the body, but
      // semibold (designer, 2026-10-06; was body2Bold). Overrides MUI's default head styling
      // (fontWeightMedium 500 + lineHeight 1.5rem/24px). Figma CellContent property1="Head".
      head: {
        fontSize: typography.body1Bold.fontSize,
        fontWeight: typography.body1Bold.fontWeight,
        lineHeight: typography.body1Bold.lineHeight,
      },
    },
  },
  MuiTableSortLabel: {
    defaultProps: {
      // Phosphor arrow replaces MUI's internal Material `ArrowDownward` (Phosphor-only icon
      // rule, docs/ICONS.md) — same approved exception used for Autocomplete/Chip icons.
      // MUI rotates this single icon between asc/desc, so one arrow covers both directions.
      IconComponent: ArrowDown,
    },
    styleOverrides: {
      icon: {
        /*
         * 12×12 (designer, 2026-09-25). MUI's default is 18px and the Figma sub-components were
         * drawn at 16 — this is a deliberate step down from both, applied to EVERY sort arrow in
         * the system (plain `Table`'s sortable headers as well as `DataTable`'s) so the affordance
         * is one size everywhere. Phosphor renders at `1em`, so the font-size drives it.
         */
        fontSize: sizes.height.h12,
        /*
         * And it keeps that size in a narrow column. The arrow is a flex item of the sort label,
         * so with the default `flex-shrink: 1` a squeezed column compressed it on the horizontal
         * axis ONLY — measured at 9×16 against its 16×16, i.e. a visibly stretched arrow rather
         * than a smaller one. An icon is not the thing that should give way when space runs out;
         * the label beside it already truncates.
         */
        flexShrink: 0,
      },
    },
  },
  // NOTE: `TablePagination` is no longer MUI's component — it is a custom composition
  // (src/components/Table/TablePagination.tsx) built from the already-themed Autocomplete /
  // TextField / IconButton, per the Figma footer redesign (node 1390:4870). It therefore needs
  // NO `MuiTablePagination` theme override — each sub-part is styled by its own component theme.

  // ── Menu family (Figma page 1462:264) ───────────────────────────────────────────────
  // SHARED NOTE: MuiMenuItem and MuiMenuList are also used by `Select` (its dropdown renders
  // MenuItems inside a MenuList) and by `TablePagination`'s rows-per-page select. These
  // overrides therefore also restyle Select's options (intentional — consistent selection/
  // hover across the system). MuiMenu.paper is scoped to Menu's own Popover Paper, so it does
  // NOT touch the global MuiPaper (SHARED by Card/Dialog/Drawer/Popover/Accordion/Table).
  MuiMenu: {
    styleOverrides: {
      paper: {
        /*
         * Width FLOOR (designer, 2026-09-21). A Menu sizes itself to its anchor, so a trigger
         * with a short label — "Type", "Status" — opened a menu narrower than its own rows were
         * comfortable to read. `maxWidth` already capped these surfaces; this is the other half.
         *
         * GLOBAL on purpose, and the blast radius was checked before it went in: it reaches
         * `SelectionChip`'s option menu, `ProductSelector`, every filter dropdown, and any
         * `Select` a consuming app renders (MUI's Select opens a Menu). It does NOT reach
         * `TablePagination`'s rows-per-page — that is an Autocomplete, which is the one place a
         * 160px floor would have looked absurd behind "10 / 25 / 50".
         *
         * A single surface that needs its own floor sets one through `slotProps.paper`.
         */
        minWidth: sizes.minWidth.xs,
        // Surface (Figma Menu): white fill, 1px neutral border, rd-4 radius.
        backgroundColor: surface.white,
        border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
        borderRadius: radius.sm,
        // Overlay elevation (Figma elevation/8) — dropdowns/menus. Explicit token, not MUI's
        // internal elevation default. Scoped to Menu's own paper, NOT global MuiPaper.
        boxShadow: shadowTokens.elevation8,
      },
    },
  },
  MuiMenuList: {
    styleOverrides: {
      root: {
        // MenuList vertical padding (Figma) — matches MUI's 8px default, kept token-explicit.
        paddingTop: spacingTokens.sp8,
        paddingBottom: spacingTokens.sp8,
        // Menu-scoped Divider spacing (Figma MenuDivider = sp8 top/bottom around a 1px line).
        // Scoped to dividers inside a MenuList so the global MuiDivider (SHARED) is untouched;
        // its colour is already palette.divider = intent.neutral.border, so only margin is set.
        '& .MuiDivider-root': {
          marginTop: spacingTokens.sp8,
          marginBottom: spacingTokens.sp8,
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Label typography = body1 (14/20). Dense → body2 (12) below.
        ...typography.body1,
        minHeight: sizes.height.h36,
        // MUI sets `minHeight: auto` at the `sm` breakpoint for non-dense MenuItems
        // (MenuItem.js), which out-orders a plain minHeight override and collapses the row to
        // the text height (~20px) on desktop (≥600px). Re-assert our row height at that
        // breakpoint so items stay h36 at all widths. (Dense keeps h32 — MUI has no sm-auto
        // rule for the dense variant, so it already holds.)
        [theme.breakpoints.up('sm')]: {
          minHeight: sizes.height.h36,
        },
        paddingLeft: spacingTokens.sp16,
        paddingRight: spacingTokens.sp16,
        // Vertical padding gives the label (+ optional subline) breathing room. With a single
        // line the row still resolves to h36 (body1 20px + sp8×2 = 36); with a subline it grows.
        paddingTop: spacingTokens.sp8,
        paddingBottom: spacingTokens.sp8,
        gap: spacingTokens.sp8,
        color: text.primary,

        // Leading icon (ListItemIcon) follows the item's text colour and is spaced by `gap`,
        // not MUI's wide default minWidth.
        '& .MuiListItemIcon-root': {
          color: 'inherit',
          minWidth: 'auto',
        },

        // Optional subline (Figma node 1470:8006): compose `<ListItemText primary secondary>`.
        // The label (primary) keeps the row's body1 + colour; the subline (secondary) is
        // captionSm / text.secondary. Scoped to MenuItem — the SHARED MuiListItemText is untouched.
        '& .MuiListItemText-root': {
          marginTop: 0,
          marginBottom: 0,
        },
        // Primary label: body1 size, but weight is INHERITED from the row so the selected
        // state (below) can bold it. Density only changes the size (dense block).
        '& .MuiListItemText-primary': {
          ...typography.body1,
          fontWeight: 'inherit',
        },
        '& .MuiListItemText-secondary': {
          ...typography.captionSm,
          color: text.secondary,
        },

        // Hover: state.hover overlay (MUI's action.hover already equals this; explicit here).
        '&:hover': {
          backgroundColor: state.hover,
        },

        // Selected: primary-tinted — same treatment as Checkbox/Radio/Switch/Table selection,
        // replacing MUI's neutral action.selected.
        '&.Mui-selected': {
          backgroundColor: intent.primary.subtle,
          color: intent.primary.text,
          // Selected label is semibold (Figma: body1Bold / body2Bold). Set the weight on the
          // row so it applies to a plain-text label AND is inherited by the ListItemText primary
          // (density picks the size → 14→body1Bold, 12→body2Bold). The subline stays regular
          // (its captionSm sets an explicit 400) and the shortcut Typography keeps its own weight.
          fontWeight: typography.body1Bold.fontWeight,
          '&:hover': { backgroundColor: intent.primary.subtle },
          '&.Mui-focusVisible': { backgroundColor: intent.primary.subtle },
          // Subline follows the selected colour (primary.text), per Figma.
          '& .MuiListItemText-secondary': { color: 'inherit' },
        },

        // Keyboard focus (Figma Focused): hover overlay + 1px primary inset border.
        '&.Mui-focusVisible': {
          backgroundColor: state.hover,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.solid}`,
        },

        // Disabled: fixed text.disabled colour (project convention — not an opacity fade).
        '&.Mui-disabled': {
          color: text.disabled,
          opacity: 1,
          // Subline dims with the row.
          '& .MuiListItemText-secondary': { color: 'inherit' },
        },

        // Dense: h32 + body2 (12px) — matches Figma Density=Dense. Tighter vertical padding
        // (sp6) so a single dense line still floors at h32; with a subline the row grows.
        '&.MuiMenuItem-dense': {
          minHeight: sizes.height.h32,
          paddingTop: spacingTokens.sp6,
          paddingBottom: spacingTokens.sp6,
          // Dense only shrinks the label SIZE to body2 (12/18); the weight is left to the row so
          // the selected-bold rule still wins (setting `...typography.body2` here would re-assert
          // weight 400 and tie with the selected 600). The subline stays captionSm (Figma).
          fontSize: typography.body2.fontSize,
          lineHeight: typography.body2.lineHeight,
          '& .MuiListItemText-primary': {
            fontSize: typography.body2.fontSize,
            lineHeight: typography.body2.lineHeight,
          },
        },
      }),
    },
  },

  // Card family (Figma "Card", node 1530:3139). Brand look: white surface, 1px neutral
  // border, rd-8, flat (no elevation). Layout (padding/gap/slots) lives on the CardContent /
  // CardActionArea slots so both the static and clickable variants match. SHARED Paper is
  // NOT touched — everything is scoped to the MuiCard* slots.
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: surface.white,
        borderWidth: sizes.border.bd1,
        borderStyle: 'solid',
        borderColor: intent.neutral.border,
        borderRadius: radius.md,
        // Clip the CardActionArea hover/ripple to the rounded corners.
        overflow: 'hidden',
        // Horizontal orientation fills its row; vertical stays content-driven.
        '&[data-orientation="horizontal"]': { width: '100%' },

        // Clickable card: hover overlay spans the WHOLE card (content + actions region),
        // not just the CardActionArea trigger. Layered over the white base so it's preserved.
        '&[data-clickable]:hover': {
          backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
        },

        // Title row: leading icon + title, top-aligned.
        '& .OxCard-titleRow': {
          display: 'flex',
          alignItems: 'flex-start',
          gap: spacingTokens.sp8,
          width: '100%',
        },
        // Leading-icon slot — sized per orientation (22 vertical / 20 horizontal).
        '& .OxCard-icon': {
          display: 'flex',
          flexShrink: 0,
          '& svg': { width: sizes.height.h22, height: sizes.height.h22 },
        },
        '&[data-orientation="horizontal"] .OxCard-icon svg': {
          width: sizes.height.h20,
          height: sizes.height.h20,
        },
        // Chips row: wraps, 6px gaps (matches the Chip slot in Figma).
        '& .OxCard-chips': {
          display: 'flex',
          flexWrap: 'wrap',
          gap: spacingTokens.sp6,
        },

        // Disabled whole-card: neutral fill, faded text/icon, non-interactive.
        '&[data-disabled]': {
          backgroundColor: intent.neutral.disabled,
          borderColor: state.disabled.border,
          pointerEvents: 'none',
          '& .MuiTypography-root': { color: text.disabled },
          // Leading icon (Phosphor uses currentColor) fades with the text.
          '& .OxCard-icon': { color: text.disabled },
        },
      },
    },
  },
  // Static content region (non-clickable card). Padding sp16/sp24, vertical stack, sp8 gap.
  MuiCardContent: {
    styleOverrides: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacingTokens.sp8,
        paddingTop: spacingTokens.sp16,
        paddingBottom: spacingTokens.sp16,
        paddingLeft: spacingTokens.sp24,
        paddingRight: spacingTokens.sp24,
        // MUI adds an extra bottom pad on the last child — normalise back to sp16.
        '&:last-child': { paddingBottom: spacingTokens.sp16 },
      },
    },
  },
  // Clickable main-action region. Same layout as CardContent; hover = state.hover overlay;
  // focus-visible = inset primary ring (intent.primary.solid) per docs/DECISIONS.md.
  MuiCardActionArea: {
    styleOverrides: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: spacingTokens.sp8,
        paddingTop: spacingTokens.sp16,
        paddingBottom: spacingTokens.sp16,
        paddingLeft: spacingTokens.sp24,
        paddingRight: spacingTokens.sp24,
        // Hover is handled on the whole Card root (see MuiCard [data-clickable]:hover) so it
        // spans the entire card, not just this trigger region. Only the focus ring lives here.
        '&.Mui-focusVisible': {
          boxShadow: `inset 0 0 0 ${sizes.border.bd2}px ${intent.primary.solid}`,
        },
      },
      // Kill MUI's built-in hover/focus overlay entirely — hover is driven on the Card root
      // and focus by the ring above. `display: none` (not `opacity: 0`) so MUI's own
      // `:hover`/`.Mui-focusVisible` rules — which only toggle opacity — can't re-show it.
      focusHighlight: { display: 'none' },
    },
  },
  // Detached supplemental-actions region. Aligns under the content padding; no top pad so
  // the sp8 flow gap from the content is preserved.
  MuiCardActions: {
    styleOverrides: {
      root: {
        gap: spacingTokens.sp8,
        paddingTop: 0,
        paddingBottom: spacingTokens.sp16,
        paddingLeft: spacingTokens.sp24,
        paddingRight: spacingTokens.sp24,
      },
    },
  },

  // ── Accordion family (Figma "Accordion" line-item, node 1596:1230) ───────────────────
  // Brand look: white bordered card, rd-8, flat (elevation 0). Padding lives on the SUMMARY,
  // Details and Actions — matching MUI's native anatomy — so the Accordion root itself has none.
  // States: `selected` (a checked Checkbox in the leading slot, matched via `:has`) recolours
  // the whole-card border to primary and tints the summary purple-subtle; hover = neutral
  // `state.hover` overlay (kept distinct from the selected tint); the focus ring lives on the
  // wrapper's caret toggle button. The header layout (leading · identifier · summary details ·
  // trailing) is expressed through the `OxAccordion-*` classes the wrapper emits, all token-bound.
  // SHARED Paper (the Accordion root) and ButtonBase are NOT themed globally — everything is
  // scoped to the MuiAccordion* slots.
  MuiAccordion: {
    defaultProps: { elevation: 0, disableGutters: true, square: true },
    styleOverrides: {
      root: {
        backgroundColor: surface.white,
        borderWidth: sizes.border.bd1,
        borderStyle: 'solid',
        borderColor: intent.neutral.border,
        borderRadius: radius.md,
        overflow: 'hidden',
        // Drop MUI's default top-divider pseudo and expanded margin — we want a flat card.
        '&::before': { display: 'none' },
        '&.Mui-expanded': { margin: 0 },

        // Selected — a checked Checkbox in the leading-controls slot recolours the card border.
        // Scoped to `.MuiCheckbox-root` (NOT a bare `input:checked`): the line-item row can also
        // carry a quick-toggle Switch in the same slot, and a Switch that is ON must NOT read as
        // "selected". Selection = the checkbox only. Do not broaden this back to `input:checked`.
        '&:has(.OxAccordion-leading .MuiCheckbox-root input:checked)': {
          borderColor: intent.primary.border,
        },

        // Summary padding / layout / states live HERE (on the intact Accordion root) rather than
        // on MuiAccordionSummary, because the wrapper swaps the summary's root slot for a
        // non-button <div> — which drops MuiAccordionSummary's own styled root (and its
        // styleOverrides). Descendant selectors keyed off `.MuiAccordionSummary-root` still apply.
        '& .MuiAccordionSummary-root': {
          paddingLeft: spacingTokens.sp24,
          paddingRight: spacingTokens.sp24,
          paddingTop: spacingTokens.sp16,
          paddingBottom: spacingTokens.sp16,
          minHeight: 'unset',
        },
        '& .MuiAccordionSummary-content': {
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: spacingTokens.sp24,
          width: '100%',
        },
        // Hover (non-selected, non-disabled) — neutral overlay, distinct from the selected tint.
        '&:not(.Mui-disabled):not(:has(.OxAccordion-leading .MuiCheckbox-root input:checked)) .MuiAccordionSummary-root:hover':
          {
            backgroundColor: state.hover,
          },
        // Selected — purple-subtle tint on the summary surface only (details/actions stay white).
        '&:has(.OxAccordion-leading .MuiCheckbox-root input:checked) .MuiAccordionSummary-root': {
          backgroundColor: intent.primary.subtle,
        },

        // Disabled row: fade the text via tokens (no MUI opacity fade); icons inherit.
        '&.Mui-disabled': {
          backgroundColor: surface.white,
          '& .MuiTypography-root': { color: text.disabled },
          '& .OxAccordion-icon': { color: text.disabled },
        },

        // ── Header layout (inside the summary content) ──
        '& .OxAccordion-leading': {
          display: 'flex',
          alignItems: 'center',
          gap: spacingTokens.sp8,
          flexShrink: 0,
        },
        // identifier + summary metadata group. `flex-wrap` makes the reflow CONTENT-driven: while
        // both fit on one line they sit side by side (column-gap sp24); when they no longer fit,
        // the metadata drops WHOLE onto the next line, under the identifier (row-gap sp16) — it
        // does NOT wrap its columns while still beside the identifier. Only once it is on its own
        // line does the metadata wrap into multiple rows (see summaryDetails). No breakpoint needed.
        '& .OxAccordion-main': {
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: spacingTokens.sp24,
          rowGap: spacingTokens.sp16,
        },
        // Identifier keeps priority: it grows to fill line 1, and because the metadata carries a
        // large flex-basis it wraps under the identifier BEFORE the identifier is squeezed — so the
        // title holds its line. On its own line (or a very narrow container) the title can wrap.
        '& .OxAccordion-identifier': {
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: spacingTokens.sp4,
        },
        '& .OxAccordion-title': {
          display: 'flex',
          alignItems: 'flex-start',
          gap: spacingTokens.sp8,
          minWidth: 0,
          width: '100%',
          // Long titles WRAP to the next line instead of overflowing onto the trailing buttons.
          // `min-width: 0` lets the text flex-item shrink below its content width; `overflow-wrap:
          // anywhere` breaks long unbroken identifiers (e.g. US-Video-(IQ)_OpenX_…) so nothing
          // spills past the row.
          '& > .MuiTypography-root': {
            flex: '1 1 auto',
            minWidth: 0,
            overflowWrap: 'anywhere',
          },
        },
        '& .OxAccordion-icon': {
          display: 'flex',
          flexShrink: 0,
          '& svg': { width: sizes.height.h20, height: sizes.height.h20 },
        },
        '& .OxAccordion-chips': {
          display: 'flex',
          flexWrap: 'wrap',
          gap: spacingTokens.sp8,
        },
        // Metadata is secondary and RIGHT-ALIGNED: `flex: 0 0 auto` (HUG — no grow, no shrink) at its
        // natural one-row content width, so the identifier's `flex-grow` fills the empty middle space
        // and pushes this block to the right, snug against the trailing controls (Figma 1596:1230).
        // Wrapping is preserved by the `auto` basis: when it can't fit beside the identifier the WHOLE
        // block wraps onto the next line (under the identifier); only once on its own line do its
        // columns wrap into multiple rows. Gap sp24 between the metadata columns (Figma).
        '& .OxAccordion-summaryDetails': {
          flex: '0 0 auto',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: spacingTokens.sp24,
        },
        '& .OxAccordion-trailing': {
          display: 'flex',
          alignItems: 'center',
          gap: spacingTokens.sp8,
          flexShrink: 0,
          // Trailing icons — the kebab menu (⋮) and the expand caret (▾) — are the neutral
          // secondary icon-button style (`text.secondary`), per Figma, regardless of what the
          // consumer passes as `menuButton`. Disabled rows keep the IconButton's own disabled colour.
          '& .MuiIconButton-root:not(.Mui-disabled)': { color: text.secondary },
        },
        // The caret toggle: 36×36 hit area, 18px glyph, rotates with the expanded state.
        '& .OxAccordion-expand': {
          width: sizes.height.h36,
          height: sizes.height.h36,
          '& svg': {
            width: sizes.height.h18,
            height: sizes.height.h18,
            transition: 'transform 150ms ease',
          },
        },
        '&.Mui-expanded .OxAccordion-expand svg': { transform: 'rotate(180deg)' },
      },
    },
  },
  // Fallback parity for RAW MuiAccordionSummary usage (button root, not the wrapper). The brand
  // Accordion wrapper swaps the summary root for a <div>, so its padding/layout/state styles live
  // on MuiAccordion.root (above) via descendant selectors instead.
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        paddingLeft: spacingTokens.sp24,
        paddingRight: spacingTokens.sp24,
        paddingTop: spacingTokens.sp16,
        paddingBottom: spacingTokens.sp16,
        minHeight: 'unset',
        '&.Mui-disabled': { opacity: 1 },
        '& .MuiAccordionSummary-content': {
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: spacingTokens.sp24,
          width: '100%',
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacingTokens.sp12,
        borderTopWidth: sizes.border.bd1,
        borderTopStyle: 'solid',
        borderTopColor: intent.neutral.border,
        paddingLeft: spacingTokens.sp24,
        paddingRight: spacingTokens.sp24,
        paddingTop: spacingTokens.sp12,
        paddingBottom: spacingTokens.sp12,
      },
    },
  },
  MuiAccordionActions: {
    // MUI's default adds `margin-left: 8px` between children (`& > :not(style) ~ :not(style)`),
    // which would stack on top of our `gap` (→ 20px). Disable it so only the `gap` spaces buttons.
    defaultProps: { disableSpacing: true },
    styleOverrides: {
      root: {
        // All actions RIGHT-ALIGNED in a single group (Figma 1596:1365). Wrap onto the next line
        // when they don't fit, instead of cramming them into one row (buttons never shrink — see
        // MuiButton). gap sp12 spaces the buttons and keeps wrapped lines apart (rowGap).
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: spacingTokens.sp12,
        borderTopWidth: sizes.border.bd1,
        borderTopStyle: 'solid',
        borderTopColor: intent.neutral.border,
        paddingLeft: spacingTokens.sp24,
        paddingRight: spacingTokens.sp24,
        paddingTop: spacingTokens.sp12,
        paddingBottom: spacingTokens.sp12,
      },
    },
  },

  // ── Tabs family (Figma node 1539:6725; supersedes the 1577:5503 spec) ───────────────
  // Two visual styles, switched by our wrapper's `appearance` prop (the wrapper adds the
  // `.AppTabs-line` / `.AppTabs-outline` class to the Tabs root — MUI's `variant` is reserved
  // for layout: standard|scrollable|fullWidth):
  //   • line    — MUI's native underline indicator. Baseline divider + a bd2 purple indicator.
  //   • outline — segmented, top-rounded bordered tabs on the same baseline, gap sp2 between,
  //               selected = purple subtle fill + purple border. Indicator is hidden.
  // Label treatment (both appearances, 2026-08-24 redesign): unselected = body1 (regular) +
  // text.secondary; SELECTED = body1Bold + intent.primary.text; disabled = regular +
  // text.disabled. Vertical padding is sp12 top AND bottom in both appearances (2026-08-26) — the
  // bottom pad doubles as the gap between the label and the baseline/indicator. Height follows from
  // padding + line-height (44) rather than a fixed token, exactly as the Figma frame hugs.
  // Hover/focus follow DS conventions (state.hover overlay + primary focus ring) — Figma's
  // raw-hex hover border (#e0dedd) and outline-hover text (#595c5f) are intentionally NOT
  // ported (no tokens); the DS overlay is used instead. `Tab` sits on `ButtonBase` (SHARED —
  // not touched here; all styling is scoped to `MuiTab`).
  MuiTabs: {
    styleOverrides: {
      root: {
        // Height is DERIVED, not pinned: `auto` drops MUI's default 48px floor so the row is as
        // tall as its tallest tab (sp12 + body1 line-height + sp12 = 44). Figma does the same —
        // the tab frame hugs its content there, with no height bound to any variable — so there is
        // deliberately no `h44` token to mirror.
        minHeight: 'auto',
        // Baseline divider under the tab row (Figma tab-group border-b bd1 neutral) — for
        // both styles. Scoped to horizontal so vertical tabs (out of Figma scope) aren't
        // given a stray bottom line.
        '&:not(.MuiTabs-vertical)': {
          borderBottom: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
        },
        // Panel style draws that baseline PER TAB instead (see MuiTab), which is the only way to
        // leave a gap in it under the selected tile: the root's border is painted below the
        // scroller, so nothing inside a tab can cover it, and the scroller's `overflow: hidden`
        // clips anything that tries to hang over it.
        '&.AppTabs-panel:not(.MuiTabs-vertical)': {
          borderBottom: 'none',
        },
      },
      // Flex container of the tabs. sp2 gap between tabs (Figma tab-group gap sp-2), both styles.
      list: {
        gap: spacingTokens.sp2,
        // Panel tiles are FLUSH — the 1px divider between them is a border, not a gap, so the
        // strip reads as one segmented surface rather than a row of separate controls.
        '.AppTabs-panel &': {
          gap: spacingTokens.none,
        },
      },
      indicator: {
        // Underline: bd2 (2px), primary. (palette.primary.main already = intent.primary.solid,
        // but set explicitly so the token intent is legible and independent of MUI defaults.)
        // `solid`, not `border` — this is a fill that has to read as "the active tab", not a soft
        // outline; the *040 border tints would render it a pale lavender smudge.
        height: sizes.border.bd2,
        backgroundColor: intent.primary.solid,
        // Outline and panel styles draw their selection as a fill — hide the shared indicator.
        '.AppTabs-outline &, .AppTabs-panel &': {
          display: 'none',
        },
      },
    },
  },
  MuiTab: {
    defaultProps: {
      // Figma (1539:6725, `Show icon`) draws the icon LEADING the label. MUI's own default is
      // `top` (Material's stacked tab), which this design system never uses — so the default is
      // flipped here rather than making every call site pass it. `top`/`bottom`/`end` still work
      // if a screen needs them, but they are off-spec.
      iconPosition: 'start',
    },
    styleOverrides: {
      root: {
        // ── both appearances ────────────────────────────────────────────
        // `auto` removes MUI's 48px minHeight floor so the tab is exactly its padding + label
        // (sp12 + 20 + sp12 = 44). See the note on MuiTabs.root: the height is derived, not a token.
        minHeight: 'auto',
        // Symmetric sp12 top and bottom (Figma 1539:6725, 2026-08-26 — was sp4 top / sp8 top for
        // outline). The bottom pad is also the gap between the label and the baseline/indicator,
        // which is what lets the 2px underline read as an underline rather than a text decoration.
        padding: `${spacingTokens.sp12}px ${spacingTokens.sp24}px`,
        textTransform: 'none',
        // Icon ↔ label spacing (Figma gap sp-6). Expressed as `gap` on the flex root rather than
        // the per-position margins MUI ships, so one token covers start/end/top/bottom instead of
        // four rules — the icon margins below are zeroed to hand spacing over to it.
        gap: spacingTokens.sp6,
        // The optional leading icon. MUI clones whatever is passed to `icon` and puts
        // `.MuiTab-icon` on it, so for a Phosphor glyph this lands on the <svg> itself.
        // Colour is deliberately NOT set: `currentColor` inherits the root's per-state colour, so
        // the icon tracks the label through default / selected / hover / disabled for free. Figma
        // binds the selected icon to `Intent/Primary/Solid` and the label to `Intent/Primary/Text`
        // — the same purple, split only because a glyph is a fill and text is text. Don't "fix"
        // this into two rules; it would be two names for one value.
        '& > .MuiTab-icon': {
          width: sizes.height.h16,
          height: sizes.height.h16,
          flexShrink: 0,
          margin: spacingTokens.none,
        },
        // Unselected tabs are the QUIET state: body1 (regular) + text.secondary. Only the
        // selected tab is bold + purple, so the active section reads at a glance.
        ...typography.body1,
        color: text.secondary,
        // Hover (DS convention): state.hover overlay via background-image so it composites over
        // any base fill and never recolours the label. Not on disabled.
        '&:not(.Mui-disabled):hover': {
          backgroundImage: `linear-gradient(${state.hover}, ${state.hover})`,
        },
        // Selected is the only weight change in the family — body1 → body1Bold. NOTE: the label
        // gets wider when it bolds (+2px measured on an 8-char label), which nudges the tabs
        // after it. Accepted as designed — see the trade-off note in Tabs.usage.md.
        '&.Mui-selected': {
          ...typography.body1Bold,
          color: intent.primary.text,
        },
        '&.Mui-disabled': {
          color: text.disabled,
        },

        // ── panel (metric strip) ────────────────────────────────────────
        // A third idiom, and deliberately not a variation of `outline`: these are TILES sitting on
        // the panel below them, not controls floating above it. Square, flush, divided by a single
        // hairline, and the selected one is `surface.white` — the same surface as the Card it sits
        // on — with the baseline cut away underneath so the tile and the panel read as one shape.
        // For a dashboard's KPI strip, where each tab is a metric and the selected one owns the
        // chart below (Figma: the consuming dashboard's metric strip; DS variant added 2026-09-01).
        '.AppTabs-panel &': {
          // A tile is roomier than a tab: sp16 top/bottom, sp32 sides (designer, 2026-09-01).
          // It has to hold two lines — a label over a 22px value — and sit as a block of surface,
          // not as a label with a hit area around it.
          padding: `${spacingTokens.sp16}px ${spacingTokens.sp32}px`,
          borderRadius: radius.none,
          backgroundColor: surface.raised,
          // The strip's own baseline, drawn per tile — see the note on MuiTabs.root.
          borderBottom: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
          // One hairline BETWEEN tiles: the last tile has nothing to its right to be divided from,
          // and a trailing edge there would read as a cut-off seventh tile.
          '&:not(:last-of-type)': {
            borderRight: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
          },
          '&.Mui-selected': {
            backgroundColor: surface.white,
            // NOT `borderBottom: none` — the box would lose 1px and the whole row would jump by a
            // pixel on every selection change. Recolouring keeps the geometry and cuts the line.
            borderBottomColor: surface.white,
          },
          '&.Mui-disabled': {
            backgroundColor: intent.neutral.disabled,
            color: text.disabled,
          },
        },

        // ── outline (segmented) ─────────────────────────────────────────
        // Ancestor selector: styled only when inside an `.AppTabs-outline` Tabs root.
        // Padding and height are now shared with `line` (both sp12) — only the chrome differs.
        // NOTE: the 1px border makes an outline tab 46px against the line tab's 44. Figma shows 44
        // for both because a Figma stroke doesn't add to a hugged frame's height, while a CSS
        // border does. Keeping padding at sp12 preserves the 12px the designer asked for; shaving
        // it to 11 to hit 44 would put a raw, untokenised value in the theme.
        '.AppTabs-outline &': {
          border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
          borderTopLeftRadius: radius.sm,
          borderTopRightRadius: radius.sm,
          backgroundColor: surface.raised,
          color: text.secondary,
          '&.Mui-selected': {
            backgroundColor: intent.primary.subtle,
            borderColor: intent.primary.border,
            color: intent.primary.text,
          },
          '&.Mui-disabled': {
            backgroundColor: intent.neutral.disabled,
            color: text.disabled,
          },
        },
      },
    },
  },

  // ── Stepper (Figma node 1578:5746) ───────────────────────────────────────────────────
  // The Figma spec defines a single Step with three states — Default (upcoming), Active
  // (current), Done (completed) — each = a 36px numbered circle + Title (body1Bold) + Subtitle
  // (caption). The circle is a CUSTOM stepIcon slot (StepperIcon) because MUI's SVG StepIcon
  // can't do a fill+border split and drops the number on completion (see StepperIcon.tsx).
  // Title colour tracks state; Subtitle stays text.secondary (inherited from labelContainer),
  // matching the rendered Figma. StepConnector is NOT in the Figma spec — styled conservatively
  // (neutral line, primary once active/completed) as a token-based best-effort.
  MuiStepLabel: {
    defaultProps: {
      // Replace MUI's SVG step icon with the DS 36px numbered circle. A caller-supplied
      // `slots` prop overrides this (MUI merges defaultProps shallowly).
      slots: { stepIcon: StepperIcon },
    },
    styleOverrides: {
      // Gap between the icon and the label = sp8 (Figma root gap). MUI default is theme.spacing(1)
      // = 8px; set explicitly via the token so it's independent of the spacing base.
      iconContainer: {
        paddingRight: spacingTokens.sp8,
      },
      // labelContainer holds the title (`label`) + the `optional` subtitle. Its colour is the
      // Subtitle colour (text.secondary in every state); the title overrides per state below.
      labelContainer: {
        color: text.secondary,
      },
      label: {
        ...typography.body1Bold,
        color: text.secondary, // Default (upcoming) title
        '&.Mui-active': { color: intent.primary.text },
        '&.Mui-completed': { color: text.primary },
        '&.Mui-error': { color: intent.error.text },
        '&.Mui-disabled': { color: text.disabled },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      // alternativeLabel places the connector across the icon's vertical centre — realign to
      // our 36px icon (MUI's default assumes the 24px SVG icon).
      alternativeLabel: {
        top: sizes.height.h36 / 2,
      },
      line: {
        borderColor: intent.neutral.border,
      },
      root: {
        '&.Mui-active .MuiStepConnector-line': { borderColor: intent.primary.solid },
        '&.Mui-completed .MuiStepConnector-line': { borderColor: intent.primary.solid },
      },
    },
  },

  // ── Dialog + Drawer surfaces (Figma Panel 1686:1080, Dialog 1659:1143, Drawer 1686:1281) ──
  // Both host the shared `Panel` chrome (header/content/footer live in src/components/Panel —
  // ONE source of truth for panel layout/dividers/spacing). Here we only style each SURFACE via
  // its own `paper` slot — this does NOT touch the global MuiPaper (SHARED by Card/Menu/Popover/
  // Accordion/Table). The backdrop scrim is bound to `surface.overlay` (same value as MUI's
  // default 50% black) — scoped per host root (`> .MuiBackdrop-root`), NOT via a global
  // MuiBackdrop override, so Menu/Popover invisible backdrops stay untouched.
  MuiDialog: {
    // Default `fullWidth` so the dialog reaches its `maxWidth` (xs=444, sm=600, md=900, lg=1200,
    // xl=1536) — MUI's maxWidth scale is used as-is.
    defaultProps: {
      fullWidth: true,
    },
    styleOverrides: {
      root: {
        '& > .MuiBackdrop-root': { backgroundColor: surface.overlay },
      },
      paper: {
        // Figma: Surface/White fill, rd-4 radius (= MUI Paper default radius, radius.sm).
        backgroundColor: surface.white,
        borderRadius: radius.sm,
        // Modal elevation (Figma elevation/24) — highest layer. Explicit token, scoped to Dialog's
        // own paper (NOT global MuiPaper).
        boxShadow: shadowTokens.elevation24,
        // fullScreen fills the viewport edge-to-edge with square corners (Figma fullScreen
        // variant). MUI already zeroes radius/margin for this class; re-assert to be safe.
        '&.MuiDialog-paperFullScreen': {
          borderRadius: radius.none,
          // Edge-to-edge surface — no elevation shadow.
          boxShadow: shadowTokens.none,
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      root: {
        // Temporary-variant scrim — same token binding as Dialog (see comment above).
        '& > .MuiBackdrop-root': { backgroundColor: surface.overlay },
      },
      paper: {
        // Figma Drawer surface: Surface/White, edge-anchored (no radius — MUI default for the
        // sliding paper). Width is set per-usage (slotProps.paper), not baked in as a token
        // (HARD RULE 2). Scoped to the Drawer's own paper — global MuiPaper untouched.
        backgroundColor: surface.white,
        // Side-sheet elevation (Figma elevation/16). Explicit token, not MUI's elevation default.
        boxShadow: shadowTokens.elevation16,
      },
    },
  },
  // Alert — brand "standard" appearance (Figma node 1636:6992): a NEUTRAL Surface/Default
  // background with a 2px coloured LEFT accent per severity — NOT MUI's per-severity tinted fill.
  // The accent + status-icon colour come from intent.*; Figma's `severity=info` renders in brand
  // purple (overriding MUI's denim `palette.info`). Scoped entirely to MuiAlert — the SHARED
  // MuiPaper (Alert's root) is deliberately NOT themed globally.
  //
  // Layout (Figma): a row of columns with a sp12 gap — status icon (own top-aligned column) ·
  // message box (title + description + optional bottom CTA) · optional right CTA (centred) ·
  // optional close ✕ (own full-height column, top-aligned). Root stays `alignItems: stretch` so
  // the icon/close columns run full height and can top-align while the right CTA centres itself.
  MuiAlert: {
    defaultProps: { variant: 'standard' },
    styleOverrides: {
      root: ({ ownerState }) => {
        // Severity accent. info = brand purple (not MUI's denim info).
        const accent =
          {
            info: intent.primary.solid,
            success: intent.success.solid,
            warning: intent.warning.solid,
            error: intent.error.solid,
          }[(ownerState.severity ?? 'info') as 'info' | 'success' | 'warning' | 'error'] ?? intent.primary.solid;
        // CTA text colour per severity (Figma) — a special-case ONLY for Alert (and later
        // Snackbar): the text Button is recoloured to the severity's `text` token instead of the
        // usual brand link. info=brand link (purple), success=green, warning=amber, error=red.
        // Not a new Button variant — scoped to the Button inside the Alert.
        const ctaColor =
          {
            info: text.link,
            success: intent.success.text,
            warning: intent.warning.text,
            error: intent.error.text,
          }[(ownerState.severity ?? 'info') as 'info' | 'success' | 'warning' | 'error'] ?? text.link;
        return {
          backgroundColor: surface.default,
          color: text.primary,
          borderRadius: radius.sm,
          borderLeft: `${sizes.border.bd2}px solid ${accent}`,
          paddingTop: spacingTokens.sp12,
          paddingBottom: spacingTokens.sp12,
          paddingLeft: spacingTokens.sp16,
          paddingRight: spacingTokens.sp16,
          alignItems: 'stretch',
          // Status icon — 20px, coloured by the severity accent, top-aligned with the title
          // (own column); sp12 gap to the message box.
          '& .MuiAlert-icon': {
            color: accent,
            fontSize: sizes.height.h20,
            opacity: 1,
            padding: 0,
            marginRight: spacingTokens.sp12,
            alignItems: 'flex-start',
          },
          // Message box — a column stacking the title, the description (body2 / Text/Secondary,
          // per Figma), and an optional bottom CTA, with a sp4 gap between them. `flex-start`
          // keeps children at their content width (so a bottom CTA doesn't stretch full-width).
          // The AlertTitle sets its own body1Bold / Text/Primary typography (see below).
          '& .MuiAlert-message': {
            ...typography.body2,
            color: text.secondary,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: spacingTokens.sp4,
            minWidth: 0,
            padding: 0,
          },
          // CTA colour rule: severity-tinted text (see `ctaColor` above). Scoped to the Button so
          // the neutral close IconButton is unaffected. Covers both placements (right column and
          // inside the message box).
          '& .MuiButton-root': {
            color: ctaColor,
          },
          // Close button — neutral grey ✕ (Figma), regardless of severity.
          '& .MuiAlert-action .MuiIconButton-root': { color: text.secondary },
          // Action column — close ✕ pinned to the top; the right CTA centres itself via the
          // wrapper Box in Alert.tsx. sp12 gap between the CTA and the ✕, sp12 to the message.
          '& .MuiAlert-action': {
            alignItems: 'flex-start',
            gap: spacingTokens.sp12,
            paddingTop: 0,
            paddingLeft: spacingTokens.sp12,
            marginRight: 0,
          },
          // Line-count-aware vertical alignment (designer request). By DEFAULT (multi-line: title +
          // description, or a wrapping description) everything top-aligns so the icon and ✕ sit on
          // the FIRST line. When Alert.tsx measures a SINGLE line of text it stamps
          // `data-single-line`, and the whole row centres vertically so the icon (and ✕ / right CTA)
          // are balanced against that one line. Scoped to MuiAlert — no SHARED blast radius.
          '&[data-single-line="true"]': {
            alignItems: 'center',
            '& .MuiAlert-icon': { alignItems: 'center' },
            '& .MuiAlert-action': { alignItems: 'center' },
          },
        };
      },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      // Title — body1Bold (14/20 semibold), Text/Primary. Spacing to the description is handled
      // by the message column's gap (sp4), so no own margins.
      root: {
        ...typography.body1Bold,
        color: text.primary,
        marginTop: 0,
        marginBottom: 0,
      },
    },
  },

  // ===== Date/Time Pickers (MUI X Community) — mirrors the Figma "Data Picker" page =====
  // No Figma tokens created; every value maps to an existing token. The popover is scoped via
  // MuiPickerPopper (NOT global MuiPaper).
  // Field: use the DS PickerField (always-visible label tab, Figma "Data Picker" field) as the
  // pickers' `textField` slot. PickerField wraps MUI X's `PickersTextField`, so v9's accessible
  // section-list field is preserved (no a11y regression). The `slots` defaultProp DOES propagate
  // to the responsive pickers' inner Desktop/Mobile field. Consumers can override per instance.
  // Icon slots use Phosphor icons ONLY (HARD RULE — see 2026-07-02 Phosphor decision). MUI X's
  // default icons (calendar/clock adornment, header nav arrows, view-switch chevron) are replaced
  // with CalendarBlank/Clock/CaretLeft/CaretRight/CaretDown via the Picker*Icon wrappers
  // (PickerSlotIcon.tsx), which strip the `ownerState` MUI X passes to slot components — a bare
  // Phosphor icon would spread it onto the <svg> and trigger React's unknown-prop warning.
  MuiDatePicker: {
    defaultProps: {
      views: [...DATE_VIEWS],
      slots: {
        textField: PickerField,
        openPickerIcon: PickerCalendarIcon,
        leftArrowIcon: PickerLeftArrowIcon,
        rightArrowIcon: PickerRightArrowIcon,
        switchViewIcon: PickerSwitchViewIcon,
      },
    },
  },
  // `DesktopDatePicker` / `MobileDatePicker` / `StaticDatePicker` are public exports, and each
  // reads its OWN theme key — the responsive `DatePicker` above resolves props and passes them
  // down, so `MuiDatePicker` never reaches a variant used directly. Repeat the view scale here or
  // `StaticDatePicker` keeps MUI's year→day jump while `DatePicker` no longer has it.
  // (Only `views` is repeated: the `slots` gap on these keys predates this change and touching it
  // would silently restyle a component nobody asked me to restyle — see docs/DECISIONS.md.)
  MuiDesktopDatePicker: {
    defaultProps: { views: [...DATE_VIEWS] },
  },
  MuiMobileDatePicker: {
    defaultProps: { views: [...DATE_VIEWS] },
  },
  MuiStaticDatePicker: {
    defaultProps: { views: [...DATE_VIEWS] },
  },
  MuiTimePicker: {
    defaultProps: {
      slots: {
        textField: PickerField,
        openPickerIcon: PickerClockIcon,
      },
    },
  },
  MuiDateTimePicker: {
    defaultProps: {
      views: [...DATE_TIME_VIEWS],
      slots: {
        textField: PickerField,
        openPickerIcon: PickerCalendarIcon,
        leftArrowIcon: PickerLeftArrowIcon,
        rightArrowIcon: PickerRightArrowIcon,
        switchViewIcon: PickerSwitchViewIcon,
      },
    },
  },
  MuiDesktopDateTimePicker: {
    defaultProps: { views: [...DATE_TIME_VIEWS] },
  },
  MuiMobileDateTimePicker: {
    defaultProps: { views: [...DATE_TIME_VIEWS] },
  },
  MuiStaticDateTimePicker: {
    defaultProps: { views: [...DATE_TIME_VIEWS] },
  },
  // The standalone FIELDS (no popover): DateField / TimeField / DateTimeField. They take the same
  // `textField` slot as the pickers above, so they get the DS label tab too — a field that looks
  // different depending on whether a calendar happens to be attached would be a plain
  // inconsistency (designer, 2026-08-13). No icon slots here: these fields have no picker to open.
  MuiDateField: {
    defaultProps: {
      slots: { textField: PickerField },
    },
  },
  MuiTimeField: {
    defaultProps: {
      slots: { textField: PickerField },
    },
  },
  MuiDateTimeField: {
    defaultProps: {
      slots: { textField: PickerField },
    },
  },
  // Standalone inline calendars (Components/Calendar) — same Phosphor nav/switch icons.
  MuiDateCalendar: {
    defaultProps: {
      views: [...DATE_VIEWS],
      slots: {
        leftArrowIcon: PickerLeftArrowIcon,
        rightArrowIcon: PickerRightArrowIcon,
        switchViewIcon: PickerSwitchViewIcon,
      },
    },
  },
  // The picker field's input (`PickersOutlinedInput`) does NOT inherit the `MuiOutlinedInput`
  // overrides (it's a separate MUI X component), so mirror the essentials here — same technique
  // as TextField: a WHITE root fill hides the label tab's slight underlap (the fieldset sits at
  // top:-5px, so the tab's -bd4 overlap would otherwise show through a transparent input), plus a
  // neutral border and `radius.sm`. Focused=primary / error=red borders come from MUI's palette.
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: surface.white,
        borderRadius: radius.sm,
        paddingLeft: spacingTokens.sp12,
        paddingRight: spacingTokens.sp12,
        height: sizes.height.h36,
        // Hover: neutral border darkened 4% (matches TextField / system hover overlay), not MUI's
        // default switch to text.primary.
        '&:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: `color-mix(in srgb, ${intent.neutral.border}, black 4%)`,
        },
        // Disabled: grayed fill + disabled border (Figma).
        '&.Mui-disabled': { backgroundColor: intent.neutral.disabled },
        '&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline': { borderColor: state.disabled.border },
        // Read-only: borderless + fill-less (the label tab already drops its bottom border).
        // NOTE: MUI X composes state classes via globalStateClasses — the class is `Mui-readOnly`
        // (NOT `MuiPickersOutlinedInput-readOnly`, unlike @mui/material's InputBase). The
        // `:not(.Mui-focused)` guard keeps the primary focus border visible while a keyboard
        // user is editing sections — read-only fields stay in the tab order.
        '&.Mui-readOnly': { backgroundColor: 'transparent' },
        '&.Mui-readOnly:not(.Mui-focused) .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
      },
      notchedOutline: {
        borderColor: intent.neutral.border,
        borderRadius: radius.sm,
      },
      // Vertical padding lives on the sections container (MUI default 16.5px). Match TextField's
      // single-line input (sp2) so the field is exactly h36 tall; horizontal padding is on root.
      sectionsContainer: {
        paddingTop: spacingTokens.sp2,
        paddingBottom: spacingTokens.sp2,
      },
    },
  },
  MuiPickerDay: {
    styleOverrides: {
      // Day cell (Figma "Day"): body1 number, pill radius, 36×36. States mirror the variant set.
      root: {
        ...typography.body1,
        color: text.primary,
        width: sizes.height.h36,
        height: sizes.height.h36,
        borderRadius: radius.pill,
        '&:hover': { backgroundColor: state.hover },
        // Today (unselected) — neutral ring (Figma State=Today). `intent.neutral.solid`, not
        // `.border` — the light grey010 was ~1.2:1 on white, below the 3:1 WCAG 1.4.11 minimum
        // for a state indicator (a11y audit 2026-07-24; mirror in Figma).
        '&.MuiPickerDay-today:not(.Mui-selected)': {
          border: `${sizes.border.bd1}px solid ${intent.neutral.solid}`,
        },
        // Outside-current-month days (Figma State=Outside) — muted.
        '&.MuiPickerDay-dayOutsideMonth': { color: text.secondary },
        // Selected (Figma State=Selected, node 1922:14602) — subtle purple fill + purple RING +
        // purple number (NOT the old solid purple fill with white text). Ring via inset boxShadow
        // so the 36×36 box doesn't shift and it composes with focus. Hold the look on hover/focus.
        '&.Mui-selected': {
          backgroundColor: intent.primary.subtle,
          color: intent.primary.text,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.border}`,
          '&:hover': { backgroundColor: intent.primary.subtle },
          '&:focus': { backgroundColor: intent.primary.subtle },
          // Keyboard focus on a selected cell — thicken the ring (bd2) so it still reads as focused
          // next to the resting 1px selected ring; subtle fill held.
          '&.Mui-focusVisible': {
            backgroundColor: intent.primary.subtle,
            boxShadow: `inset 0 0 0 ${sizes.border.bd2}px ${intent.primary.solid}`,
          },
        },
        // Keyboard focus on an unselected cell — DS focus-ring convention (MuiMenuItem).
        '&.Mui-focusVisible:not(.Mui-selected)': {
          backgroundColor: state.hover,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.solid}`,
        },
        // Disabled (Figma State=Disabled) — faded text.
        '&.Mui-disabled': { color: text.disabled },
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      // Month/year label — body1Bold (Figma header).
      label: { ...typography.body1Bold, color: text.primary },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      // Prev/next nav arrows — neutral grey (Figma), matching MUI's action.active default.
      button: { color: text.secondary },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      // Weekday header labels (S M T W T F S) — caption, secondary (Figma weekday row).
      weekDayLabel: { ...typography.caption, color: text.secondary },
    },
  },
  MuiPickerPopper: {
    styleOverrides: {
      // Popover surface — white paper, dropdown radius, overlay elevation (Figma elevation/8) to
      // match Menu/Autocomplete. Explicit token; scoped here, NOT global MuiPaper.
      paper: {
        backgroundColor: surface.white,
        borderRadius: radius.md,
        boxShadow: shadowTokens.elevation8,
      },
    },
  },
  // Month/year cells are BARE <button>s (no ButtonBase → no `.Mui-focusVisible` class and MUI
  // zeroes the outline), so the focus indicator uses the CSS `:focus-visible` pseudo-class:
  // unselected → DS inset ring (MuiMenuItem convention); selected → thicker (bd2) purple ring.
  MuiMonthCalendar: {
    styleOverrides: {
      // Selected month cell — subtle purple fill + purple ring + purple label (Figma 1922:14602),
      // matching the day cell. NOT a solid purple fill.
      root: {
        '& button:focus-visible': {
          backgroundColor: state.hover,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.solid}`,
        },
        '& .Mui-selected': {
          backgroundColor: intent.primary.subtle,
          color: intent.primary.text,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.border}`,
          '&:hover': { backgroundColor: intent.primary.subtle },
          '&:focus': { backgroundColor: intent.primary.subtle },
          '&:focus-visible': {
            backgroundColor: intent.primary.subtle,
            boxShadow: `inset 0 0 0 ${sizes.border.bd2}px ${intent.primary.solid}`,
          },
        },
      },
    },
  },
  MuiYearCalendar: {
    styleOverrides: {
      // Selected year cell — subtle purple fill + purple ring + purple label (Figma 1922:14602),
      // matching the day cell. NOT a solid purple fill.
      root: {
        '& button:focus-visible': {
          backgroundColor: state.hover,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.solid}`,
        },
        '& .Mui-selected': {
          backgroundColor: intent.primary.subtle,
          color: intent.primary.text,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.primary.border}`,
          '&:hover': { backgroundColor: intent.primary.subtle },
          '&:focus': { backgroundColor: intent.primary.subtle },
          '&:focus-visible': {
            backgroundColor: intent.primary.subtle,
            boxShadow: `inset 0 0 0 ${sizes.border.bd2}px ${intent.primary.solid}`,
          },
        },
      },
    },
  },
  // Time selection (TimePicker / DateTimePicker). MUI's DigitalClock + MultiSectionDigitalClock
  // render the selected hour/minute/meridiem as a SOLID purple fill + white text by default.
  // Unify with the calendar's selected story (Figma 1922:14602): subtle purple fill + purple
  // label. These are LIST ROWS (not pill cells), so — like MuiTableRow/MenuItem selection — they
  // take the tint only, no ring. Scoped to the clock item slots; nothing else is affected.
  MuiDigitalClock: {
    styleOverrides: {
      item: {
        '&.Mui-selected': {
          backgroundColor: intent.primary.subtle,
          color: intent.primary.text,
          '&:hover': { backgroundColor: intent.primary.subtle },
          '&:focus': { backgroundColor: intent.primary.subtle },
        },
      },
    },
  },
  MuiMultiSectionDigitalClockSection: {
    styleOverrides: {
      item: {
        '&.Mui-selected': {
          backgroundColor: intent.primary.subtle,
          color: intent.primary.text,
          '&:hover': { backgroundColor: intent.primary.subtle },
          '&:focus': { backgroundColor: intent.primary.subtle },
        },
      },
    },
  },
  // Tooltip — Figma 2174:11036 (Design System 2.0). Surface = inverse (midnight blue) with white
  // caption text, arrow matched to the surface. Component-scoped: Tooltip builds on `Popper`
  // (NOT Popover/Paper/Modal), so this touches NO SHARED primitive — no ripple risk.
  MuiTooltip: {
    styleOverrides: {
      // `tooltip` = the label wrapper (`.MuiTooltip-tooltip`). Figma: bg surface/inverse,
      // text/white, padding sp4×sp8, radius rd-4, typography captionSm, max-width 240 (rd xs).
      tooltip: {
        backgroundColor: surface.inverse,
        color: text.white,
        borderRadius: radius.sm,
        padding: `${spacingTokens.sp4}px ${spacingTokens.sp8}px`,
        maxWidth: sizes.maxWidth.xs,
        ...typography.captionSm,
        // `color="white"` (EXCH-19613) — the `Tooltip` wrapper puts `data-color` on this slot.
        // A white tooltip needs an outline or it vanishes on white cards: bd1 intent/neutral/border,
        // drawn as an inset shadow (not a border) so both colours keep the exact same size.
        '&[data-color="white"]': {
          backgroundColor: surface.white,
          color: text.primary,
          boxShadow: `inset 0 0 0 ${sizes.border.bd1}px ${intent.neutral.border}`,
          // The arrow is painted over the tooltip edge (covering the outline where they meet), so
          // the rotated square carries the same outline on its two visible edges.
          '& .MuiTooltip-arrow': {
            color: surface.white,
            '&::before': {
              border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
            },
          },
          // MUI hangs the arrow just outside the tooltip edge (-0.71em margin per side). Pull it
          // bd1 inward so its white fill covers the outline across the arrow's base.
          '.MuiTooltip-popper[data-popper-placement*="top"] & .MuiTooltip-arrow': {
            marginBottom: `calc(-0.71em + ${sizes.border.bd1}px)`,
          },
          '.MuiTooltip-popper[data-popper-placement*="bottom"] & .MuiTooltip-arrow': {
            marginTop: `calc(-0.71em + ${sizes.border.bd1}px)`,
          },
          '.MuiTooltip-popper[data-popper-placement*="left"] & .MuiTooltip-arrow': {
            marginInlineEnd: `calc(-0.71em + ${sizes.border.bd1}px)`,
          },
          '.MuiTooltip-popper[data-popper-placement*="right"] & .MuiTooltip-arrow': {
            marginInlineStart: `calc(-0.71em + ${sizes.border.bd1}px)`,
          },
        },
      },
      // `arrow` paints from `color` (MUI sets the arrow's ::before background to currentColor).
      arrow: {
        color: surface.inverse,
      },
    },
  },
};
